import { useState, useEffect, useMemo, useRef } from 'react';
import {
  fetchCountries, fetchAlerts, fetchCrises, sendChatMessage,
  searchAll, fetchAllAirlines, fetchAirportDetail,
  CountryResponse, AlertResponse, CrisisResponse,
  SearchResult, AirlineRef, AirportDetailResponse,
} from './api/client';

// ═══════════════════════════════════════════════════════════════
// FLIGHTSOPEN.AI — v5 Apple-Clean Redesign
// One input. One answer. One screen at a time.
// ═══════════════════════════════════════════════════════════════

// Retry helper with exponential backoff for flaky Lambda cold-starts
async function retryFetch<T>(fn: () => Promise<T>, retries = 3): Promise<T> {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await fn();
    } catch {
      if (attempt === retries - 1) throw new Error('retryFetch: exhausted retries');
      await new Promise(r => setTimeout(r, 1000 * 2 ** attempt));
    }
  }
  throw new Error('retryFetch: unreachable');
}

const c = {
  bg: '#0B1120', card: '#0F1A2E', blue: '#2B7FFF', blueL: '#5BA0FF',
  cyan: '#00D4AA', white: '#FFFFFF', sky: '#F0F4FA',
  amber: '#FFB020', red: '#FF4757', green: '#00C48C',
  s50: '#64748B', s40: '#94A3B8', s30: '#CBD5E1',
  bdr: 'rgba(43,127,255,0.1)', bdrH: 'rgba(43,127,255,0.25)',
};
const f = { d: "'Lexend',sans-serif", b: "'Albert Sans',sans-serif", m: "'JetBrains Mono',monospace" };

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800&family=Albert+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
*{margin:0;padding:0;box-sizing:border-box}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes slideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
@keyframes spin{to{transform:rotate(360deg)}}
::placeholder{color:${c.s50}}
::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:${c.s50};border-radius:2px}
.sel{background:${c.card};border:1px solid ${c.bdr};border-radius:8px;color:${c.sky};padding:0 14px;font-family:'Albert Sans',sans-serif;outline:none;cursor:pointer}
.sel:focus{border-color:${c.blue}}
`;

// ── Types ──────────────────────────────────────────────────────
interface Route {
  to: string;
  s: 'operating' | 'limited' | 'suspended';
  freq: string;
  t: string;
}
interface Airline {
  code: string;
  name: string;
  routes: Route[];
}
interface Airport {
  code: string;
  name: string;
  city: string;
  country: string;
  iso: string;
  flag: string;
  lat: number;
  lon: number;
  level: number;
  status: 'open' | 'partial' | 'closed';
  airlines: Airline[];
}

// ── Helpers ────────────────────────────────────────────────────
function isoToFlag(iso: string): string {
  const offset = 127397;
  try {
    return Array.from(iso.toUpperCase())
      .map(ch => String.fromCodePoint(ch.charCodeAt(0) + offset))
      .join('');
  } catch {
    return '🌍';
  }
}

function FlagImg({ iso, size = 26 }: { iso: string; size?: number }) {
  if (!iso) return <span style={{ fontSize: size }}>🌍</span>;
  const code = iso.toLowerCase();
  return (
    <img
      src={`https://flagcdn.com/w80/${code}.png`}
      srcSet={`https://flagcdn.com/w80/${code}.png 1x, https://flagcdn.com/w160/${code}.png 2x`}
      alt={iso.toUpperCase()}
      style={{ width: size, height: Math.round(size * 0.75), objectFit: 'cover', borderRadius: 3, verticalAlign: 'middle' }}
      onError={(e) => { const t = e.target as HTMLImageElement; t.style.display = 'none'; t.insertAdjacentText('afterend', isoToFlag(iso)); }}
    />
  );
}

function levelToStatus(level: number): Airport['status'] {
  // Advisory-based PLACEHOLDER for the list view before real-time data loads.
  // This is deliberately conservative: advisory level reflects country travel
  // risk, NOT airport operational status. Many L4 countries (Russia, Turkey,
  // Iran) have fully operational airports. Real-time data from the API
  // (via operationalToStatus) overrides this as soon as it loads.
  if (level >= 3) return 'partial';
  return 'open';
}

/** Map the API's operational_status to the frontend status value.
 *  'unknown' means no real-time data — returns null so the caller can decide. */
function operationalToStatus(opStatus: string | undefined | null): Airport['status'] | null {
  if (opStatus === 'closed') return 'closed';
  if (opStatus === 'disrupted') return 'partial';
  if (opStatus === 'unknown' || !opStatus) return null;
  return 'open';
}

function alertStatusToRouteStatus(status: AlertResponse['status']): Route['s'] {
  if (status === 'suspended' || status === 'cancelled') return 'suspended';
  if (status === 'resumed') return 'operating';
  return 'limited'; // delayed, rerouted, diverted
}

function parseRouteDest(route: string | null): string {
  if (!route) return '—';
  const m = route.match(/[A-Z]{3}-([A-Z]{3})/);
  if (m && m[1]) return m[1];
  const parts = route.split(/[-–]/);
  const last = parts[parts.length - 1];
  if (parts.length >= 2 && last) return last.trim().slice(0, 3);
  return route.slice(0, 3) || '—';
}

/**
 * Build airline data from the API's airlines_operating array (reference routes + alerts overlay).
 * Falls back to constructing from recent_alerts if airlines_operating is not present.
 */
function buildAirlinesFromDetail(detail: AirportDetailResponse): Airline[] {
  // Prefer the enriched airlines_operating data from the backend
  if (detail.airlines_operating && detail.airlines_operating.length > 0) {
    return detail.airlines_operating.map(al => ({
      code: al.code,
      name: al.name || al.code,
      routes: al.routes.map(r => {
        const status: Route['s'] = r.status === 'suspended' || r.status === 'cancelled'
          ? 'suspended' : r.status === 'limited' || r.status === 'delayed' || r.status === 'diverted' || r.status === 'rerouted'
          ? 'limited' : r.status === 'unknown'
          ? 'suspended' : 'operating';
        const ts = r.last_updated ? new Date(r.last_updated) : null;
        const timeStr = ts && !isNaN(ts.getTime())
          ? `${String(ts.getUTCHours()).padStart(2, '0')}:${String(ts.getUTCMinutes()).padStart(2, '0')} UTC`
          : '—';
        return { to: r.to, s: status, freq: '—', t: timeStr };
      }),
    }));
  }

  // Fallback: reconstruct from recent_alerts (legacy path)
  return (detail.recent_alerts || []).reduce<Airline[]>((acc, al) => {
    let airline = acc.find(a => a.code === al.airline_iata);
    if (!airline) {
      airline = { code: al.airline_iata, name: al.airline_name || al.airline_iata, routes: [] };
      acc.push(airline);
    }
    const dest = parseRouteDest(al.route);
    const routeStatus = alertStatusToRouteStatus(al.status as AlertResponse['status']);
    if (dest !== '—' && !airline.routes.some(r => r.to === dest && r.s === routeStatus)) {
      const ts = new Date(al.last_updated);
      const timeStr = isNaN(ts.getTime()) ? '—' : `${String(ts.getUTCHours()).padStart(2, '0')}:${String(ts.getUTCMinutes()).padStart(2, '0')} UTC`;
      airline.routes.push({ to: dest, s: routeStatus, freq: '—', t: timeStr });
    }
    return acc;
  }, []);
}

// Lat/lon table for haversine (key Middle East + surrounding airports)
const AIRPORT_COORDS: Record<string, [number, number]> = {
  DXB:[25.25,55.36], AUH:[24.44,54.65], DOH:[25.26,51.61], IKA:[35.41,51.15],
  THR:[35.69,51.31], TLV:[32.01,34.87], IST:[41.26,28.74], GYD:[40.47,50.05],
  AMM:[31.72,35.99], BAH:[26.27,50.63], RUH:[24.96,46.70], JED:[21.67,39.16],
  BGW:[33.26,44.23], EBL:[36.24,43.96], CAI:[30.12,31.40], MCT:[23.59,58.28],
  KWI:[29.23,47.97], EVN:[40.15,44.40], TBS:[41.67,44.95], SHJ:[25.33,55.52],
  LHR:[51.47,-0.46], CDG:[49.01,2.55], JFK:[40.64,-73.78], BKK:[13.69,100.75],
  DEL:[28.56,77.10], SIN:[1.36,103.99], BOM:[19.09,72.87], MHD:[36.24,59.64],
  AHB:[18.24,42.66], TIF:[21.47,40.54], AKH:[27.89,45.72], DMM:[26.47,49.80],
  KHI:[24.91,67.17], LHE:[31.52,74.40], ISB:[33.62,73.10], KBL:[34.57,69.21],
  DAM:[33.41,36.52], BEY:[33.82,35.49], SAH:[15.48,44.22], ADE:[12.83,45.03],
};

function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

// Build airport list from API data
function buildAirports(countries: CountryResponse[], alerts: AlertResponse[]): Airport[] {
  const airportMap = new Map<string, Airport>();

  // Index countries by ISO for quick lookup
  const countryByIso = new Map<string, CountryResponse>();
  for (const c of countries) countryByIso.set(c.iso, c);

  // Build airports from alerts (each alert carries airport metadata)
  for (const alert of alerts) {
    const iata = alert.airport_iata;
    if (!iata) continue;

    const countryIso = alert.country_iso || '';
    const country = countryByIso.get(countryIso);
    const level = alert.advisory_level ?? country?.advisory_level ?? 2;

    if (!airportMap.has(iata)) {
      const coords: [number, number] = AIRPORT_COORDS[iata] ?? [0, 0];
      airportMap.set(iata, {
        code: iata,
        name: alert.airport_name || iata,
        city: alert.airport_city || '',
        country: country?.name || countryIso,
        iso: countryIso,
        flag: countryIso ? isoToFlag(countryIso) : '🌍',
        lat: coords[0],
        lon: coords[1],
        level,
        status: levelToStatus(level),
        airlines: [],
      });
    }

    const ap = airportMap.get(iata)!;

    // Update level to max seen
    if (level > ap.level) {
      ap.level = level;
      ap.status = levelToStatus(level);
    }

    // Add airline if not present
    const airlineCode = alert.airline_iata;
    const airlineName = alert.airline_name || airlineCode;
    let airline = ap.airlines.find(al => al.code === airlineCode);
    if (!airline) {
      airline = { code: airlineCode, name: airlineName, routes: [] };
      ap.airlines.push(airline);
    }

    // Add route
    const dest = parseRouteDest(alert.route);
    const routeStatus = alertStatusToRouteStatus(alert.status);
    const routeExists = airline.routes.some(r => r.to === dest && r.s === routeStatus);
    if (!routeExists && dest !== '—') {
      const ts = new Date(alert.last_updated);
      const timeStr = isNaN(ts.getTime())
        ? '—'
        : `${String(ts.getUTCHours()).padStart(2, '0')}:${String(ts.getUTCMinutes()).padStart(2, '0')} UTC`;
      airline.routes.push({
        to: dest,
        s: routeStatus,
        freq: '—',
        t: timeStr,
      });
    }
  }

  // Build IATA → name/city lookup from airport_details (returned by /countries API)
  const airportNameMap = new Map<string, { name: string; city: string }>();
  for (const country of countries) {
    if (country.airport_details) {
      for (const ad of country.airport_details) {
        airportNameMap.set(ad.iata, { name: ad.name, city: ad.city });
      }
    }
  }

  // Also seed airports from country airport lists that have no alerts yet
  // Include ALL countries (not just L2+) so L1 airports like DXB are clickable from search
  for (const country of countries) {
    for (const iata of country.airports) {
      if (!airportMap.has(iata)) {
        const detail = airportNameMap.get(iata);
        const coords: [number, number] = AIRPORT_COORDS[iata] ?? [0, 0];
        airportMap.set(iata, {
          code: iata,
          name: detail?.name ?? iata,
          city: detail?.city ?? '',
          country: country.name,
          iso: country.iso,
          flag: isoToFlag(country.iso),
          lat: coords[0],
          lon: coords[1],
          level: country.advisory_level,
          status: levelToStatus(country.advisory_level),
          airlines: [],
        });
      }
    }
  }

  return Array.from(airportMap.values()).sort((a, b) => b.level - a.level || a.code.localeCompare(b.code));
}

// ── Status helpers ─────────────────────────────────────────────
const ST: Record<string, { label: string; color: string; bg: string }> = {
  operating: { label: 'Operating', color: c.green, bg: 'rgba(0,196,140,.1)' },
  limited:   { label: 'Limited',   color: c.amber, bg: 'rgba(255,176,32,.1)' },
  suspended: { label: 'Suspended', color: c.red,   bg: 'rgba(255,71,87,.1)' },
  open:      { label: 'Open',      color: c.green, bg: 'rgba(0,196,140,.1)' },
  partial:   { label: 'Partial',   color: c.amber, bg: 'rgba(255,176,32,.1)' },
  closed:    { label: 'Closed',    color: c.red,   bg: 'rgba(255,71,87,.1)' },
};

const PILL_FALLBACK = { label: 'Open', color: c.green, bg: 'rgba(0,196,140,.1)' };

const Pill = ({ s }: { s: string }) => {
  const v = ST[s] ?? PILL_FALLBACK;
  return (
    <span style={{ display:'inline-flex',alignItems:'center',gap:5,padding:'3px 10px',fontSize:11,fontFamily:f.m,fontWeight:600,color:v.color,background:v.bg,borderRadius:20,letterSpacing:'.3px' }}>
      <span style={{ width:6,height:6,borderRadius:'50%',background:v.color }} />{v.label}
    </span>
  );
};

// ── Icons ──────────────────────────────────────────────────────
const Ic = {
  Search: ({ s=20 }: { s?: number }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c.s50} strokeWidth="1.5" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>,
  Back: ({ s=20 }: { s?: number }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c.s40} strokeWidth="1.5" strokeLinecap="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>,
  Bell: ({ s=18 }: { s?: number }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M18 8A6 6 0 106 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>,
  Compass: ({ s=18 }: { s?: number }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c.cyan} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88" fill={c.cyan} fillOpacity=".15" stroke={c.cyan}/></svg>,
  Near: ({ s=16 }: { s?: number }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c.cyan} strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v2m0 16v2m10-10h-2M4 12H2m15.07-7.07l-1.41 1.41M8.34 15.66l-1.41 1.41m0-12.74l1.41 1.41m7.32 7.32l1.41 1.41"/></svg>,
  Check: ({ s=14 }: { s?: number }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c.green} strokeWidth="2" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>,
  Dollar: ({ s=14 }: { s?: number }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c.cyan} strokeWidth="1.5" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6"/></svg>,
  Send: ({ s=16 }: { s?: number }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c.white} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4z"/></svg>,
  X: ({ s=18 }: { s?: number }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c.s40} strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>,
  Clock: ({ s=11 }: { s?: number }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c.s50} strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
  MapPin: ({ s=16, col }: { s?: number; col?: string }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={col||c.blue} strokeWidth="1.5" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1118 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  Globe: ({ s=16, col }: { s?: number; col?: string }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={col||c.s40} strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2z"/></svg>,
};

const Logo = ({ s=20 }: { s?: number }) => (
  <div style={{ display:'flex',alignItems:'center' }}>
    <span style={{ fontFamily:f.d,fontSize:s,fontWeight:800,color:c.white,letterSpacing:'-.5px',lineHeight:1 }}>Flights</span>
    <span style={{ display:'inline-block',position:'relative',width:s*.7,height:s,margin:`0 ${s*.06}px 0 ${s*.08}px` }}>
      <svg viewBox="0 0 30 42" width={s*.7} height={s} style={{ position:'absolute',top:0,left:0 }} overflow="visible">
        <circle cx="15" cy="21" r="15" fill="none" stroke={c.blue} strokeWidth="7"/>
        <g transform="translate(19,17) rotate(-40)"><path d="M12,-1L-8,-10L-4,0Z" fill={c.cyan}/><path d="M12,-1L-8,8L-4,0Z" fill={c.cyan} opacity=".6"/></g>
      </svg>
    </span>
    <span style={{ fontFamily:f.d,fontSize:s,fontWeight:800,color:c.blue,letterSpacing:'-.5px',lineHeight:1 }}>pen</span>
    <span style={{ fontFamily:f.d,fontSize:s*.55,fontWeight:400,color:c.s50,lineHeight:1 }}>.ai</span>
  </div>
);

// ── CompData type ──────────────────────────────────────────────
interface CompData {
  from?: string;
  code?: string;
  airline?: string;
  to?: string;
}

// ── Chat message type ──────────────────────────────────────────
interface ChatMsg { r: 'u' | 'a'; t: string }

// ══════════════════════════════════════════════════════════════
export default function App() {
  const [query, setQuery]           = useState('');
  const [selected, setSelected]     = useState<string | null>(null);
  const [alertKeys, setAlertKeys]   = useState<string[]>([]);
  const [chatOpen, setChatOpen]     = useState(false);
  const [chatMsg, setChatMsg]       = useState('');
  const [chatMsgs, setChatMsgs]     = useState<ChatMsg[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [compData, setCompData]     = useState<CompData | null>(null);
  const [view, setView]             = useState<'search'|'alerts'|'crises'|'refund'|'feedback'>('search');
  const [userLoc, setUserLoc]       = useState<{ lat: number; lon: number } | null>(null);
  const [locLoading, setLocLoading] = useState(false);
  const [showMap, setShowMap]       = useState(false);
  const [fbText, setFbText]         = useState('');
  const [fbType, setFbType]         = useState('suggestion');
  const [fbSent, setFbSent]         = useState(false);
  const [fbItems, setFbItems]       = useState([
    { id:1, type:'suggestion', text:'Add WhatsApp notification channel for alerts', votes:24, voted:false, ts:'2 days ago' },
    { id:2, type:'suggestion', text:'Show historical route status timeline — was this route open yesterday?', votes:18, voted:false, ts:'3 days ago' },
    { id:3, type:'bug', text:"Search doesn't match airline names with spaces (e.g. 'Iran Air')", votes:7, voted:false, ts:'1 day ago' },
    { id:4, type:'suggestion', text:'Add Farsi language support for Iran airports', votes:31, voted:false, ts:'4 days ago' },
    { id:5, type:'suggestion', text:'Browser push notifications when a subscribed route reopens', votes:15, voted:false, ts:'2 days ago' },
  ]);

  // API data
  const [airports, setAirports]   = useState<Airport[]>([]);
  const [crises, setCrises]       = useState<CrisisResponse[]>([]);
  const [countries, setCountries] = useState<CountryResponse[]>([]);
  const [allAirlinesRef, setAllAirlinesRef] = useState<AirlineRef[]>([]);
  const [loading, setLoading]     = useState(true);
  const [apiError, setApiError]   = useState<string | false>(false);

  // Search state: uses backend search API for full coverage
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refund typeahead state
  const [refundAirportQ, setRefundAirportQ] = useState('');
  const [refundAirportOpen, setRefundAirportOpen] = useState(false);
  const [refundAirportResults, setRefundAirportResults] = useState<{ iata: string; name: string; city?: string; country_iso?: string }[]>([]);
  const [refundAirlineQ, setRefundAirlineQ] = useState('');
  const [refundAirlineOpen, setRefundAirlineOpen] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch data on mount — each endpoint isolated so partial data still renders
  useEffect(() => {
    let cancelled = false;

    async function fetchWithRetry<T>(fn: () => Promise<T>, label: string, retries = 3): Promise<T> {
      for (let attempt = 0; attempt < retries; attempt++) {
        try {
          return await fn();
        } catch (err) {
          console.warn(`[FlightsOpen] ${label} attempt ${attempt + 1}/${retries} failed:`, err);
          if (attempt === retries - 1) throw err;
          await new Promise(r => setTimeout(r, 1000 * 2 ** attempt));
        }
      }
      throw new Error(`${label}: exhausted retries`);
    }

    (async () => {
      const errors: string[] = [];

      // Load countries + crises first (fast) — render UI immediately
      const [countriesResult, crisesResult] = await Promise.all([
        fetchWithRetry(fetchCountries, 'countries')
          .catch((err: unknown) => { errors.push(`countries: ${err instanceof Error ? err.message : String(err)}`); return [] as CountryResponse[]; }),
        fetchWithRetry(fetchCrises, 'crises')
          .catch((err: unknown) => { errors.push(`crises: ${err instanceof Error ? err.message : String(err)}`); return [] as CrisisResponse[]; }),
      ]);

      if (cancelled) return;

      // Render UI now with countries data — don't wait for alerts
      try {
        setAirports(buildAirports(countriesResult, []));
      } catch (err) {
        console.error('[FlightsOpen] buildAirports failed:', err);
        errors.push(`build: ${err instanceof Error ? err.message : String(err)}`);
      }

      setCountries(countriesResult);
      setCrises(crisesResult);

      if (countriesResult.length === 0 && errors.length > 0) {
        setApiError(errors.join(' | '));
      }

      setLoading(false);

      // Load alerts + airlines in background
      fetchWithRetry(fetchAlerts, 'alerts')
        .then((alertsResult) => {
          if (cancelled) return;
          try {
            setAirports(buildAirports(countriesResult, alertsResult));
          } catch (err) {
            console.error('[FlightsOpen] buildAirports with alerts failed:', err);
          }
        })
        .catch((err: unknown) => {
          console.warn('[FlightsOpen] alerts failed (non-blocking):', err);
        });

      fetchWithRetry(fetchAllAirlines, 'airlines')
        .then((airlinesResult) => {
          if (cancelled) return;
          setAllAirlinesRef(airlinesResult);
        })
        .catch((err: unknown) => {
          console.warn('[FlightsOpen] airlines failed (non-blocking):', err);
        });
    })();

    return () => { cancelled = true; };
  }, []);

  // Enrich airport names — fetch real names for top impacted airports that only have IATA codes
  useEffect(() => {
    const needEnrichment = airports
      .filter(a => a.level >= 3 && a.name === a.code)
      .sort((a, b) => {
        const aRoutes = a.airlines.reduce((s, al) => s + al.routes.length, 0);
        const bRoutes = b.airlines.reduce((s, al) => s + al.routes.length, 0);
        if (bRoutes !== aRoutes) return bRoutes - aRoutes;
        return b.level - a.level;
      })
      .slice(0, 12);

    if (needEnrichment.length === 0) return;

    let cancelled = false;
    Promise.allSettled(
      needEnrichment.map(a =>
        retryFetch(() => fetchAirportDetail(a.code))
          .then(detail => ({ code: a.code, name: detail.name, city: detail.city }))
          .catch(() => null)
      )
    ).then(results => {
      if (cancelled) return;
      const nameMap = new Map<string, { name: string; city: string }>();
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value) {
          nameMap.set(r.value.code, { name: r.value.name, city: r.value.city });
        }
      }
      if (nameMap.size === 0) return;
      setAirports(prev => prev.map(a => {
        const enriched = nameMap.get(a.code);
        if (enriched) return { ...a, name: enriched.name, city: enriched.city };
        return a;
      }));
    });

    return () => { cancelled = true; };
  }, [airports.filter(a => a.level >= 3 && a.name === a.code).length]);

  // Debounced search using backend API for full coverage
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    searchTimer.current = setTimeout(() => {
      searchAll(trimmed)
        .then(results => { setSearchResults(results); setSearchLoading(false); })
        .catch(() => { setSearchResults([]); setSearchLoading(false); });
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [query]);

  // Refund airport autocomplete — use backend search for full global coverage
  const refundSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (refundSearchTimer.current) clearTimeout(refundSearchTimer.current);
    if (refundAirportQ.length < 2) { setRefundAirportResults([]); return; }
    refundSearchTimer.current = setTimeout(() => {
      searchAll(refundAirportQ)
        .then(results => {
          setRefundAirportResults(
            results.filter(r => r.kind === 'airport').map(r => ({
              iata: r.iata || '', name: r.name, city: r.city, country_iso: r.country_iso,
            })).slice(0, 12)
          );
        })
        .catch(() => setRefundAirportResults([]));
    }, 300);
    return () => { if (refundSearchTimer.current) clearTimeout(refundSearchTimer.current); };
  }, [refundAirportQ]);

  const getLocation = () => {
    if (userLoc) return;
    setLocLoading(true);
    navigator.geolocation?.getCurrentPosition(
      pos => { setUserLoc({ lat: pos.coords.latitude, lon: pos.coords.longitude }); setLocLoading(false); },
      () => setLocLoading(false),
      { timeout: 8000 }
    );
  };

  const q = query.toLowerCase().trim();

  const ap = selected ? airports.find(a => a.code === selected) ?? null : null;

  const nearby = useMemo(() => {
    if (!ap || ap.lat === 0) return [];
    return airports
      .filter(a => a.code !== ap.code && a.status !== 'closed' && a.lat !== 0)
      .map(a => ({ ...a, dist: haversine(ap.lat, ap.lon, a.lat, a.lon) }))
      .filter(a => a.dist < 1000)
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 5);
  }, [ap, airports]);

  const nearbyFromMe = useMemo(() => {
    if (!userLoc) return [];
    return airports
      .filter(a => a.lat !== 0)
      .map(a => ({ ...a, dist: haversine(userLoc.lat, userLoc.lon, a.lat, a.lon) }))
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 8);
  }, [userLoc, airports]);

  // Enrich nearby airport names when airport detail is viewed
  useEffect(() => {
    const toEnrich = nearby.filter(n => n.name === n.code).map(n => n.code);
    if (toEnrich.length === 0) return;
    let cancelled = false;
    Promise.allSettled(
      toEnrich.map(code =>
        retryFetch(() => fetchAirportDetail(code))
          .then(d => ({ code, name: d.name, city: d.city }))
          .catch(() => null)
      )
    ).then(results => {
      if (cancelled) return;
      const nameMap = new Map<string, { name: string; city: string }>();
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value) nameMap.set(r.value.code, { name: r.value.name, city: r.value.city });
      }
      if (nameMap.size === 0) return;
      setAirports(prev => prev.map(a => {
        const enriched = nameMap.get(a.code);
        if (enriched) return { ...a, name: enriched.name, city: enriched.city };
        return a;
      }));
    });
    return () => { cancelled = true; };
  }, [nearby.map(n => n.code).join(',')]);

  const toggleAlert = (key: string) =>
    setAlertKeys(p => p.includes(key) ? p.filter(a => a !== key) : [...p, key]);
  const hasAlert = (key: string) => alertKeys.includes(key);

  const doChat = async () => {
    if (!chatMsg.trim() || chatLoading) return;
    const msg = chatMsg;
    setChatMsg('');
    setChatMsgs(p => [...p, { r: 'u', t: msg }]);
    setChatLoading(true);
    try {
      const res = await sendChatMessage(msg);
      setChatMsgs(p => [...p, { r: 'a', t: res.reply }]);
    } catch {
      setChatMsgs(p => [...p, { r: 'a', t: "Sorry, I couldn't connect right now. Please try again." }]);
    } finally {
      setChatLoading(false);
    }
  };

  const openAirport = (code: string) => {
    setSelected(code);
    setView('search');
    window.scrollTo(0, 0);

    // If airport data is incomplete (no airlines loaded yet), fetch full detail from backend.
    // Always fetch when airlines are empty — the countries API provides names/cities but not airlines.
    const existing = airports.find(a => a.code === code);
    if (!existing || existing.name === code || existing.airlines.length === 0) {
      fetchAirportDetail(code)
        .then((detail: AirportDetailResponse) => {
          setAirports(prev => {
            const copy = [...prev];
            const idx = copy.findIndex(a => a.code === code);
            const coords: [number, number] = AIRPORT_COORDS[code] ?? [detail.lat ?? 0, detail.lon ?? 0];
            const level = detail.advisory_level ?? existing?.level ?? 1;
            // Use the API's operational_status (driven by real-time data).
            // When no real-time data exists, show 'open' as a safe default —
            // advisory level reflects country risk, not airport operations.
            const mapped = operationalToStatus(detail.operational_status);
            const status = mapped ?? 'open' as Airport['status'];
            const enriched: Airport = {
              code,
              name: detail.name || code,
              city: detail.city || '',
              country: detail.country_name || existing?.country || '',
              iso: detail.country_iso || existing?.iso || '',
              flag: detail.country_iso ? isoToFlag(detail.country_iso) : existing?.flag || '🌍',
              lat: coords[0] || detail.lat || 0,
              lon: coords[1] || detail.lon || 0,
              level,
              status,
              airlines: existing?.airlines?.length ? existing.airlines : buildAirlinesFromDetail(detail),
            };
            if (idx >= 0) {
              copy[idx] = enriched;
            } else {
              copy.push(enriched);
            }
            return copy;
          });
        })
        .catch((err: unknown) => console.warn('[FlightsOpen] airport detail fetch failed:', err));
    }
  };
  const goHome = () => { setSelected(null); setQuery(''); setView('search'); };

  const activeRoutes = (a: Airport) => a.airlines.reduce((s, al) => s + al.routes.filter(r => r.s !== 'suspended').length, 0);

  // Unique airlines across all airports for refund dropdown
  const allAirlines = useMemo(() => {
    const map = new Map<string, string>();
    airports.forEach(ap => ap.airlines.forEach(al => { if (!map.has(al.code)) map.set(al.code, al.name); }));
    return Array.from(map.entries()).map(([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [airports]);

  return (
    <div style={{ minHeight:'100vh',background:c.bg,fontFamily:f.b,color:c.sky }}>
      <style>{CSS}</style>

      {/* ─── NAV ─── */}
      <nav style={{ position:'sticky',top:0,zIndex:50,display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0 24px',height:52,background:`${c.card}ee`,borderBottom:`1px solid ${c.bdr}`,backdropFilter:'blur(12px)' }}>
        <div onClick={goHome} style={{ cursor:'pointer' }}><Logo s={18} /></div>
        <div style={{ display:'flex',alignItems:'center',gap:2 }}>
          {([
            { id:'search', l:'Airports' },
            { id:'refund', l:'Refund Check' },
            { id:'crises', l:'Crises' },
            { id:'alerts', l:'Alerts', n:alertKeys.length },
            { id:'feedback', l:'Feedback' },
          ] as { id: typeof view; l: string; n?: number }[]).map(n => (
            <button key={n.id} onClick={() => { setView(n.id); setSelected(null); }} style={{ padding:'6px 14px',fontFamily:f.b,fontSize:12.5,fontWeight:view===n.id?600:400,color:view===n.id?c.white:c.s50,background:'none',border:'none',cursor:'pointer',borderRadius:6,transition:'color .15s' }}>
              {n.l}
              {(n.n ?? 0) > 0 && <span style={{ marginLeft:5,fontFamily:f.m,fontSize:9,color:c.blue,background:'rgba(43,127,255,.12)',padding:'1px 6px',borderRadius:10 }}>{n.n}</span>}
            </button>
          ))}
        </div>
      </nav>

      {/* ─── MAIN ─── */}
      <main style={{ maxWidth:800,margin:'0 auto',padding:'0 24px 100px' }}>

        {/* Loading state */}
        {loading && (
          <div style={{ display:'flex',alignItems:'center',justifyContent:'center',paddingTop:120,flexDirection:'column',gap:16 }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={c.blue} strokeWidth="2" strokeLinecap="round" style={{ animation:'spin 1s linear infinite' }}><path d="M12 2v4m0 12v4m10-10h-4M6 12H2"/></svg>
            <span style={{ fontFamily:f.b,fontSize:14,color:c.s40 }}>Loading flight data…</span>
          </div>
        )}

        {/* Error state */}
        {!loading && apiError && (
          <div style={{ textAlign:'center',paddingTop:80 }}>
            <div style={{ fontFamily:f.d,fontSize:16,color:c.s40,marginBottom:8 }}>Unable to load data</div>
            <div style={{ fontFamily:f.b,fontSize:13,color:c.s50,marginBottom:16 }}>Check your connection and refresh the page.</div>
            <div style={{ fontFamily:f.m,fontSize:11,color:c.s50,opacity:.7,maxWidth:400,margin:'0 auto',wordBreak:'break-all' }}>{apiError}</div>
            <button onClick={() => window.location.reload()} style={{ marginTop:16,padding:'8px 20px',background:c.blue,color:c.white,border:'none',borderRadius:8,fontFamily:f.b,fontSize:13,cursor:'pointer' }}>Retry</button>
          </div>
        )}

        {!loading && !apiError && (
          <>
            {/* ═══ SEARCH VIEW ═══ */}
            {view === 'search' && !selected && (
              <div style={{ animation:'fadeIn .3s ease' }}>
                <div style={{ paddingTop:q||userLoc ? 28 : 80, paddingBottom:q ? 16 : 24, textAlign:'center', transition:'padding .3s ease' }}>
                  {!q && !userLoc && (
                    <>
                      <h1 style={{ fontFamily:f.d,fontSize:30,fontWeight:700,color:c.white,marginBottom:8,letterSpacing:'-.5px' }}>Find what's flying from your airport</h1>
                      <p style={{ fontFamily:f.b,fontSize:15,color:c.s40,marginBottom:28 }}>Or the next best option nearby — right now</p>
                    </>
                  )}
                  {!q && userLoc && (
                    <>
                      <h1 style={{ fontFamily:f.d,fontSize:26,fontWeight:700,color:c.white,marginBottom:6,letterSpacing:'-.5px' }}>Airports near you</h1>
                      <p style={{ fontFamily:f.b,fontSize:14,color:c.s40,marginBottom:20 }}>Sorted by distance from your location</p>
                    </>
                  )}
                  <div style={{ position:'relative',maxWidth:520,margin:'0 auto' }}>
                    <div style={{ display:'flex',alignItems:'center',gap:10,padding:'0 18px',height:52,background:c.card,border:`1.5px solid ${q ? c.blue : c.bdr}`,borderRadius:14,boxShadow:q ? '0 0 30px rgba(43,127,255,.08)' : 'none',transition:'all .2s' }}>
                      <Ic.Search s={20} />
                      <input
                        ref={inputRef}
                        value={query}
                        onChange={e => { setQuery(e.target.value); if (e.target.value) setUserLoc(null); }}
                        placeholder="Search city, airport, or airline..."
                        autoFocus
                        style={{ flex:1,background:'none',border:'none',outline:'none',fontFamily:f.b,fontSize:16,color:c.white,letterSpacing:'-.2px' }}
                      />
                      {query && <button onClick={() => setQuery('')} style={{ background:'none',border:'none',cursor:'pointer',padding:4 }}><Ic.X s={16} /></button>}
                    </div>
                    {!q && (
                      <div style={{ marginTop:12 }}>
                        <button onClick={getLocation} disabled={locLoading} style={{ display:'inline-flex',alignItems:'center',gap:6,padding:'8px 16px',fontFamily:f.b,fontSize:13,fontWeight:500,color:userLoc?c.green:c.blue,background:userLoc?'rgba(0,196,140,.08)':'rgba(43,127,255,.06)',border:`1px solid ${userLoc?'rgba(0,196,140,.2)':'rgba(43,127,255,.15)'}`,borderRadius:20,cursor:locLoading?'wait':'pointer',transition:'all .15s' }}>
                          {locLoading ? (
                            <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c.blue} strokeWidth="2" strokeLinecap="round" style={{ animation:'spin .8s linear infinite' }}><path d="M12 2v4m0 12v4m10-10h-4M6 12H2"/></svg> Finding you...</>
                          ) : userLoc ? (
                            <><Ic.Check s={14} /> Location found</>
                          ) : (
                            <><Ic.MapPin s={14} /> Use my location</>
                          )}
                        </button>
                        {userLoc && (
                          <button onClick={() => setShowMap(m => !m)} style={{ marginLeft:8,display:'inline-flex',alignItems:'center',gap:5,padding:'8px 14px',fontFamily:f.b,fontSize:13,fontWeight:500,color:showMap?c.cyan:c.s40,background:showMap?'rgba(0,212,170,.08)':'none',border:`1px solid ${showMap?'rgba(0,212,170,.2)':c.bdr}`,borderRadius:20,cursor:'pointer',transition:'all .15s' }}>
                            <Ic.MapPin s={14} col={showMap?c.cyan:c.s40} /> {showMap ? 'Hide map' : 'Show map'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Map view */}
                {showMap && userLoc && !q && (
                  <div style={{ maxWidth:520,margin:'0 auto 20px',borderRadius:14,overflow:'hidden',border:`1px solid ${c.bdr}`,animation:'slideUp .3s ease' }}>
                    <a href={`https://www.google.com/maps/@${userLoc.lat},${userLoc.lon},6z`} target="_blank" rel="noopener noreferrer" style={{ display:'block',height:200,background:`linear-gradient(135deg, ${c.card} 0%, rgba(43,127,255,.08) 100%)`,position:'relative',cursor:'pointer',textDecoration:'none',overflow:'hidden' }}>
                      <div style={{ position:'absolute',inset:0,opacity:.06,backgroundImage:`linear-gradient(${c.blue} 1px, transparent 1px), linear-gradient(90deg, ${c.blue} 1px, transparent 1px)`,backgroundSize:'40px 40px' }}/>
                      {nearbyFromMe.slice(0,6).map((a, i) => {
                        const x = 15 + (i % 3) * 30;
                        const y = 20 + Math.floor(i / 3) * 35;
                        return (
                          <div key={a.code} style={{ position:'absolute',left:`${x}%`,top:`${y}%`,display:'flex',flexDirection:'column',alignItems:'center',gap:2 }}>
                            <div style={{ width:10,height:10,borderRadius:'50%',background:a.status==='open'?c.green:a.status==='closed'?c.red:c.amber,boxShadow:`0 0 8px ${a.status==='open'?c.green:a.status==='closed'?c.red:c.amber}60` }}/>
                            <span style={{ fontFamily:f.m,fontSize:8,color:c.s30,fontWeight:600 }}>{a.code}</span>
                          </div>
                        );
                      })}
                      <div style={{ position:'absolute',left:'50%',top:'50%',transform:'translate(-50%,-50%)',display:'flex',flexDirection:'column',alignItems:'center',gap:2 }}>
                        <div style={{ width:14,height:14,borderRadius:'50%',background:c.blue,border:`3px solid ${c.white}`,boxShadow:`0 0 12px ${c.blue}80` }}/>
                        <span style={{ fontFamily:f.m,fontSize:8,color:c.white,fontWeight:600 }}>YOU</span>
                      </div>
                      <div style={{ position:'absolute',bottom:10,right:12,display:'flex',alignItems:'center',gap:4,padding:'4px 10px',background:'rgba(11,17,32,.7)',borderRadius:6,fontFamily:f.m,fontSize:9,color:c.s30 }}>
                        Open in Google Maps ↗
                      </div>
                    </a>
                    <div style={{ padding:'10px 14px',background:c.card,display:'flex',flexWrap:'wrap',gap:6 }}>
                      {nearbyFromMe.slice(0,6).map(a => (
                        <button key={a.code} onClick={() => openAirport(a.code)} style={{ display:'inline-flex',alignItems:'center',gap:5,padding:'5px 10px',fontFamily:f.m,fontSize:10,color:c.blueL,background:c.bg,border:`1px solid ${c.bdr}`,borderRadius:20,cursor:'pointer' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = c.bdrH; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = c.bdr; }}>
                          <FlagImg iso={a.iso} size={18} /> {a.code} <span style={{ color:c.s50 }}>{a.dist}km</span> <Pill s={a.status}/>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick actions */}
                <div style={{ maxWidth:520,margin:'0 auto',display:'flex',gap:10,marginBottom:q ? 16 : 20 }}>
                  <button onClick={() => { setView('refund'); setSelected(null); }} style={{ flex:1,display:'flex',alignItems:'center',gap:12,padding:'14px 16px',background:`linear-gradient(135deg, rgba(0,212,170,.06) 0%, rgba(43,127,255,.04) 100%)`,border:'1.5px solid rgba(0,212,170,.2)',borderRadius:14,cursor:'pointer',textAlign:'left',transition:'all .2s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,212,170,.4)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(0,212,170,.2)'; e.currentTarget.style.transform = 'none'; }}>
                    <div style={{ width:40,height:40,borderRadius:10,background:'rgba(0,212,170,.1)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                      <Ic.Dollar s={20} />
                    </div>
                    <div>
                      <div style={{ fontFamily:f.d,fontSize:13,fontWeight:600,color:c.white,marginBottom:2 }}>Flight cancelled?</div>
                      <div style={{ fontFamily:f.b,fontSize:11.5,color:c.s40 }}>Check if you're owed up to <span style={{ color:c.cyan,fontWeight:600 }}>€600</span></div>
                    </div>
                  </button>
                  <button onClick={() => setView('alerts')} style={{ flex:1,display:'flex',alignItems:'center',gap:12,padding:'14px 16px',background:'rgba(43,127,255,.04)',border:`1.5px solid rgba(43,127,255,.15)`,borderRadius:14,cursor:'pointer',textAlign:'left',transition:'all .2s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(43,127,255,.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(43,127,255,.15)'; e.currentTarget.style.transform = 'none'; }}>
                    <div style={{ width:40,height:40,borderRadius:10,background:'rgba(43,127,255,.1)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                      <Ic.Bell s={20} />
                    </div>
                    <div>
                      <div style={{ fontFamily:f.d,fontSize:13,fontWeight:600,color:c.white,marginBottom:2 }}>Get flight alerts</div>
                      <div style={{ fontFamily:f.b,fontSize:11.5,color:c.s40 }}>Subscribe to airports & airlines{alertKeys.length > 0 ? ` · ${alertKeys.length} active` : ''}</div>
                    </div>
                  </button>
                </div>

                {/* Nearby from location */}
                {!q && userLoc && nearbyFromMe.length > 0 && (
                  <div style={{ maxWidth:520,margin:'0 auto',animation:'slideUp .3s ease both' }}>
                    <div style={{ fontFamily:f.m,fontSize:10,color:c.s50,textTransform:'uppercase',letterSpacing:1.5,marginBottom:12 }}>Nearest airports from you</div>
                    <div style={{ display:'flex',flexDirection:'column',gap:6 }}>
                      {nearbyFromMe.map((a, i) => (
                        <button key={a.code} onClick={() => openAirport(a.code)} style={{ display:'flex',alignItems:'center',gap:14,padding:'12px 16px',background:c.card,border:`1px solid ${c.bdr}`,borderRadius:12,cursor:'pointer',textAlign:'left',width:'100%',transition:'all .15s',animationDelay:`${i*.03}s` }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = c.bdrH; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = c.bdr; }}>
                          <span style={{ lineHeight:1,width:40,textAlign:'center' }}><FlagImg iso={a.iso} size={28} /></span>
                          <div style={{ flex:1,minWidth:0 }}>
                            <div style={{ display:'flex',alignItems:'center',gap:6,marginBottom:2 }}>
                              <span style={{ fontFamily:f.m,fontSize:15,fontWeight:600,color:c.blueL }}>{a.code}</span>
                              <span style={{ fontFamily:f.d,fontSize:13,fontWeight:500,color:c.white }}>{a.city || a.name}</span>
                            </div>
                            <div style={{ fontFamily:f.b,fontSize:11,color:c.s50 }}>{a.country}</div>
                          </div>
                          <div style={{ textAlign:'right' }}>
                            <div style={{ fontFamily:f.m,fontSize:13,fontWeight:600,color:c.cyan }}>{a.dist} km</div>
                            <Pill s={a.status} />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search results — uses backend search for full coverage */}
                {q && (
                  <div style={{ maxWidth:520,margin:'0 auto' }}>
                    {searchLoading && (
                      <div style={{ display:'flex',alignItems:'center',gap:8,padding:'20px 0',justifyContent:'center' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c.blue} strokeWidth="2" strokeLinecap="round" style={{ animation:'spin .8s linear infinite' }}><path d="M12 2v4m0 12v4m10-10h-4M6 12H2"/></svg>
                        <span style={{ fontFamily:f.b,fontSize:13,color:c.s50 }}>Searching…</span>
                      </div>
                    )}
                    {!searchLoading && searchResults.length === 0 && q.length >= 2 && (
                      <div style={{ textAlign:'center',padding:'40px 0',color:c.s50 }}>
                        <div style={{ fontSize:14,marginBottom:4 }}>No results for "{query}"</div>
                        <div style={{ fontSize:12 }}>Try a city name, IATA code, country, or airline</div>
                      </div>
                    )}
                    {!searchLoading && searchResults.length > 0 && (
                      <>
                        <div style={{ fontFamily:f.m,fontSize:10,color:c.s50,marginBottom:12 }}>{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</div>
                        <div style={{ display:'flex',flexDirection:'column',gap:6 }}>
                          {searchResults.map((r, i) => {
                            if (r.kind === 'airport') {
                              const localAp = airports.find(a => a.code === r.iata);
                              return (
                                <button key={`ap-${r.iata}`} onClick={() => openAirport(r.iata!)} style={{ display:'flex',alignItems:'center',gap:14,padding:'14px 16px',background:c.card,border:`1px solid ${c.bdr}`,borderRadius:12,cursor:'pointer',textAlign:'left',transition:'all .15s',animation:`slideUp .3s ease both ${i * .04}s`,width:'100%' }}
                                  onMouseEnter={e => { e.currentTarget.style.borderColor = c.bdrH; }}
                                  onMouseLeave={e => { e.currentTarget.style.borderColor = c.bdr; }}>
                                  <span style={{ lineHeight:1,width:44,textAlign:'center' }}><FlagImg iso={r.country_iso || ''} size={32} /></span>
                                  <div style={{ flex:1,minWidth:0 }}>
                                    <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:2 }}>
                                      <span style={{ fontFamily:f.m,fontSize:16,fontWeight:600,color:c.blueL,letterSpacing:'.04em' }}>{r.iata}</span>
                                      <span style={{ fontFamily:f.d,fontSize:14,fontWeight:500,color:c.white }}>{r.city || r.name}</span>
                                    </div>
                                    <div style={{ fontFamily:f.b,fontSize:12,color:c.s50 }}>{r.name}{r.type ? ` · ${r.type.replace(/_/g,' ')}` : ''}</div>
                                  </div>
                                  <div style={{ display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4 }}>
                                    {(() => {
                                      // Only show status badge when we have real-time data.
                                      // Advisory level reflects country travel risk, NOT airport ops.
                                      const mapped = operationalToStatus(r.operational_status);
                                      if (mapped) return <Pill s={mapped} />;
                                      if (localAp) return <Pill s={localAp.status} />;
                                      return null;
                                    })()}
                                  </div>
                                </button>
                              );
                            }
                            if (r.kind === 'country') {
                              const lvl = r.advisory_level ?? 1;
                              return (
                                <button key={`co-${r.iso}`} onClick={() => { /* Navigate to country alerts if available */ const cAps = airports.filter(a => a.iso === r.iso); if (cAps.length > 0 && cAps[0]) openAirport(cAps[0].code); }} style={{ display:'flex',alignItems:'center',gap:14,padding:'14px 16px',background:c.card,border:`1px solid ${c.bdr}`,borderRadius:12,cursor:'pointer',textAlign:'left',transition:'all .15s',animation:`slideUp .3s ease both ${i * .04}s`,width:'100%' }}
                                  onMouseEnter={e => { e.currentTarget.style.borderColor = c.bdrH; }}
                                  onMouseLeave={e => { e.currentTarget.style.borderColor = c.bdr; }}>
                                  <span style={{ lineHeight:1,width:44,textAlign:'center' }}><FlagImg iso={r.iso || ''} size={32} /></span>
                                  <div style={{ flex:1,minWidth:0 }}>
                                    <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:2 }}>
                                      <span style={{ fontFamily:f.d,fontSize:15,fontWeight:600,color:c.white }}>{r.name}</span>
                                      <span style={{ fontFamily:f.m,fontSize:9,color:lvl>=4?c.red:lvl>=3?c.amber:lvl>=2?'#FFD700':c.green,background:lvl>=4?'rgba(255,71,87,.08)':lvl>=3?'rgba(255,176,32,.08)':lvl>=2?'rgba(255,215,0,.08)':'rgba(0,196,140,.08)',padding:'2px 8px',borderRadius:10 }}>L{lvl}</span>
                                    </div>
                                    <div style={{ fontFamily:f.b,fontSize:12,color:c.s50 }}>{r.iso} · Country</div>
                                  </div>
                                  <span style={{ fontFamily:f.m,fontSize:10,color:c.s50 }}>→</span>
                                </button>
                              );
                            }
                            if (r.kind === 'airline') {
                              return (
                                <button key={`al-${r.iata}`} onClick={() => {}} style={{ display:'flex',alignItems:'center',gap:14,padding:'14px 16px',background:c.card,border:`1px solid ${c.bdr}`,borderRadius:12,cursor:'pointer',textAlign:'left',transition:'all .15s',animation:`slideUp .3s ease both ${i * .04}s`,width:'100%' }}
                                  onMouseEnter={e => { e.currentTarget.style.borderColor = c.bdrH; }}
                                  onMouseLeave={e => { e.currentTarget.style.borderColor = c.bdr; }}>
                                  <span style={{ fontSize:28,lineHeight:1,width:44,textAlign:'center' }}>✈</span>
                                  <div style={{ flex:1,minWidth:0 }}>
                                    <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:2 }}>
                                      <span style={{ fontFamily:f.m,fontSize:15,fontWeight:600,color:c.blueL }}>{r.iata}</span>
                                      <span style={{ fontFamily:f.d,fontSize:14,fontWeight:500,color:c.white }}>{r.name}</span>
                                    </div>
                                    <div style={{ fontFamily:f.b,fontSize:12,color:c.s50 }}>{r.country || 'Airline'}</div>
                                  </div>
                                  <span style={{ fontFamily:f.m,fontSize:9,color:c.blue,background:'rgba(43,127,255,.08)',padding:'2px 8px',borderRadius:10 }}>Airline</span>
                                </button>
                              );
                            }
                            return null;
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Most impacted airports */}
                {!q && !userLoc && (
                  <div style={{ maxWidth:520,margin:'0 auto',marginTop:28,animation:'slideUp .4s ease both .1s' }}>
                    <div style={{ fontFamily:f.m,fontSize:10,color:c.s50,textTransform:'uppercase',letterSpacing:1.5,marginBottom:12 }}>Most impacted airports</div>
                    {airports.filter(a => a.level >= 3).sort((a, b) => {
                      // Prefer airports with real names over IATA-as-name
                      const aHasName = a.name !== a.code ? 1 : 0;
                      const bHasName = b.name !== b.code ? 1 : 0;
                      if (aHasName !== bHasName) return bHasName - aHasName;
                      // Then sort by number of airline alerts (most disrupted first)
                      const aRoutes = a.airlines.reduce((s, al) => s + al.routes.length, 0);
                      const bRoutes = b.airlines.reduce((s, al) => s + al.routes.length, 0);
                      if (bRoutes !== aRoutes) return bRoutes - aRoutes;
                      // Then by severity level (higher = more impacted)
                      return b.level - a.level;
                    }).slice(0, 6).map((a) => (
                      <button key={a.code} onClick={() => openAirport(a.code)} style={{ display:'flex',alignItems:'center',gap:14,padding:'12px 16px',background:'transparent',border:'none',borderBottom:`1px solid ${c.bdr}`,cursor:'pointer',textAlign:'left',width:'100%',transition:'background .1s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(43,127,255,.04)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                        <span style={{ lineHeight:1,width:36,textAlign:'center' }}><FlagImg iso={a.iso} size={26} /></span>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                            <span style={{ fontFamily:f.m,fontSize:14,fontWeight:600,color:c.blueL }}>{a.code}</span>
                            <span style={{ fontFamily:f.d,fontSize:13,color:c.white }}>{a.city || a.name}</span>
                          </div>
                          <span style={{ fontFamily:f.b,fontSize:11,color:c.s50 }}>{a.country}</span>
                        </div>
                        <Pill s={a.status} />
                      </button>
                    ))}
                    {airports.filter(a => a.level >= 3).length === 0 && (
                      <div style={{ textAlign:'center',padding:'32px 0',color:c.s50,fontFamily:f.b,fontSize:13 }}>No significantly impacted airports at this time.</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ═══ AIRPORT DETAIL ═══ */}
            {view === 'search' && selected && ap && (
              <div style={{ animation:'slideUp .3s ease both',paddingTop:20 }}>
                <button onClick={() => setSelected(null)} style={{ display:'flex',alignItems:'center',gap:6,padding:'8px 0',fontFamily:f.b,fontSize:13,color:c.s40,background:'none',border:'none',cursor:'pointer',marginBottom:16 }}>
                  <Ic.Back s={18} /> Back
                </button>

                <div style={{ display:'flex',alignItems:'center',gap:16,marginBottom:32 }}>
                  <span style={{ lineHeight:1 }}><FlagImg iso={ap.iso} size={48} /></span>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:4 }}>
                      <span style={{ fontFamily:f.m,fontSize:32,fontWeight:700,color:c.blueL,letterSpacing:'.04em' }}>{ap.code}</span>
                      <Pill s={ap.status} />
                    </div>
                    <div style={{ fontFamily:f.d,fontSize:17,fontWeight:500,color:c.white }}>{ap.name}</div>
                    <div style={{ fontFamily:f.b,fontSize:13,color:c.s50 }}>{ap.city}{ap.city && ap.country ? ', ' : ''}{ap.country}</div>
                  </div>
                  <button onClick={() => toggleAlert(ap.code)} style={{ display:'flex',alignItems:'center',gap:5,padding:'8px 14px',fontFamily:f.b,fontSize:12,fontWeight:500,color:hasAlert(ap.code)?c.blue:c.s40,background:hasAlert(ap.code)?'rgba(43,127,255,.1)':'none',border:`1px solid ${hasAlert(ap.code)?c.blue+'40':c.bdr}`,borderRadius:8,cursor:'pointer',transition:'all .15s' }}>
                    <Ic.Bell s={15} />{hasAlert(ap.code) ? 'Alerting' : 'Set Alert'}
                  </button>
                </div>

                <div style={{ display:'flex',gap:8,marginBottom:28 }}>
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ap.name + ', ' + ap.city)}`} target="_blank" rel="noopener noreferrer" style={{ display:'flex',alignItems:'center',gap:6,padding:'10px 16px',fontFamily:f.b,fontSize:12.5,fontWeight:500,color:c.blue,background:'rgba(43,127,255,.06)',border:`1px solid rgba(43,127,255,.15)`,borderRadius:10,cursor:'pointer',textDecoration:'none',transition:'all .15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(43,127,255,.35)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(43,127,255,.15)'; }}>
                    <Ic.MapPin s={15} /> Get directions
                  </a>
                  {ap.lat !== 0 && (
                    <a href={`https://www.google.com/maps/search/?api=1&query=${ap.lat},${ap.lon}`} target="_blank" rel="noopener noreferrer" style={{ display:'flex',alignItems:'center',gap:6,padding:'10px 16px',fontFamily:f.b,fontSize:12.5,fontWeight:500,color:c.s40,background:'none',border:`1px solid ${c.bdr}`,borderRadius:10,cursor:'pointer',textDecoration:'none',transition:'all .15s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = c.bdrH; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = c.bdr; }}>
                      <Ic.Globe s={15} /> View on map
                    </a>
                  )}
                </div>

                <div style={{ marginBottom:40 }}>
                  <h3 style={{ fontFamily:f.d,fontSize:15,fontWeight:600,color:c.white,marginBottom:4 }}>Airlines & Routes</h3>
                  <p style={{ fontFamily:f.b,fontSize:12,color:c.s50,marginBottom:16 }}>
                    {ap.airlines.length > 0
                      ? ap.status === 'closed'
                        ? `${ap.airlines.length} airline${ap.airlines.length > 1 ? 's' : ''} · all suspended`
                        : ap.status === 'partial'
                        ? `${ap.airlines.length} airline${ap.airlines.length > 1 ? 's' : ''} · status unverified`
                        : `${ap.airlines.length} airline${ap.airlines.length > 1 ? 's' : ''} operating · ${ap.airlines.reduce((n, al) => n + al.routes.length, 0)} routes`
                      : ''}
                  </p>
                  {ap.airlines.length === 0 && (
                    <div style={{ fontFamily:f.b,fontSize:13,color:c.s50,padding:'20px 0' }}>Loading airline data...</div>
                  )}
                  {ap.airlines.map(al => (
                    <div key={al.code} style={{ marginBottom:16,background:c.card,borderRadius:12,border:`1px solid ${c.bdr}`,overflow:'hidden' }}>
                      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px' }}>
                        <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                          <span style={{ fontFamily:f.m,fontSize:13,fontWeight:600,color:c.blueL }}>{al.code}</span>
                          <span style={{ fontFamily:f.b,fontSize:14,color:c.white }}>{al.name}</span>
                        </div>
                        <button onClick={() => toggleAlert(al.code)} style={{ fontFamily:f.m,fontSize:9,color:hasAlert(al.code)?c.blue:c.s50,background:hasAlert(al.code)?'rgba(43,127,255,.1)':'none',border:`1px solid ${c.bdr}`,borderRadius:20,padding:'3px 10px',cursor:'pointer',display:'flex',alignItems:'center',gap:4 }}>
                          <Ic.Bell s={10} />{hasAlert(al.code) ? 'On' : 'Alert'}
                        </button>
                      </div>
                      {al.routes.map((rt, ri) => (
                        <div key={ri} style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 16px',borderTop:`1px solid ${c.bdr}` }}>
                          <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                            <span style={{ fontFamily:f.m,fontSize:13,color:c.sky,minWidth:90 }}>{ap.code} → {rt.to}</span>
                            <Pill s={rt.s} />
                            <span style={{ fontFamily:f.b,fontSize:11,color:c.s50 }}>{rt.freq}</span>
                          </div>
                          <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                            <span style={{ fontFamily:f.m,fontSize:9,color:c.s50,display:'flex',alignItems:'center',gap:3 }}><Ic.Clock />{rt.t}</span>
                            {rt.s === 'suspended' && (
                              <button onClick={() => setCompData({ airline:al.name,code:al.code,from:ap.code,to:rt.to })} style={{ fontFamily:f.m,fontSize:9,color:c.cyan,background:'rgba(0,212,170,.08)',border:'1px solid rgba(0,212,170,.2)',borderRadius:20,padding:'3px 10px',cursor:'pointer',display:'flex',alignItems:'center',gap:3 }}>
                                <Ic.Dollar /> Compensation
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {nearby.length > 0 && (
                  <div>
                    <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:6 }}>
                      <Ic.Near />
                      <h3 style={{ fontFamily:f.d,fontSize:15,fontWeight:600,color:c.white }}>Nearest Open Airports</h3>
                    </div>
                    <p style={{ fontFamily:f.b,fontSize:12,color:c.s50,marginBottom:16 }}>Alternatives with operating flights</p>
                    <div style={{ display:'flex',flexDirection:'column',gap:6 }}>
                      {nearby.map(n => (
                        <button key={n.code} onClick={() => openAirport(n.code)} style={{ display:'flex',alignItems:'center',gap:14,padding:'12px 16px',background:c.card,border:`1px solid ${c.bdr}`,borderRadius:12,cursor:'pointer',textAlign:'left',width:'100%',transition:'border .15s' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = c.bdrH; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = c.bdr; }}>
                          <FlagImg iso={n.iso} size={26} />
                          <div style={{ flex:1 }}>
                            <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                              <span style={{ fontFamily:f.m,fontSize:14,fontWeight:600,color:c.blueL }}>{n.code}</span>
                              <span style={{ fontFamily:f.d,fontSize:13,color:c.white }}>{n.city || n.name}</span>
                              <Pill s={n.status} />
                            </div>
                            <span style={{ fontFamily:f.b,fontSize:11,color:c.s50 }}>{n.country}</span>
                          </div>
                          <div style={{ textAlign:'right' }}>
                            <div style={{ fontFamily:f.m,fontSize:13,fontWeight:600,color:c.cyan }}>{n.dist} km</div>
                            <div style={{ fontFamily:f.m,fontSize:10,color:c.s50 }}>{activeRoutes(n)} routes</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ═══ REFUND CHECK ═══ */}
            {view === 'refund' && (
              <div style={{ animation:'fadeIn .3s ease',paddingTop:40,maxWidth:520,margin:'0 auto' }}>
                <h2 style={{ fontFamily:f.d,fontSize:22,fontWeight:700,color:c.white,marginBottom:6 }}>Check Your Refund</h2>
                <p style={{ fontFamily:f.b,fontSize:13,color:c.s50,marginBottom:28 }}>Flight cancelled or delayed? You may be owed compensation.</p>

                <div style={{ marginBottom:20,position:'relative' }}>
                  <label style={{ fontFamily:f.b,fontSize:12,fontWeight:500,color:c.s40,display:'block',marginBottom:6 }}>Departure airport</label>
                  <input
                    value={refundAirportQ || (compData?.from ? `${compData.from} — ${airports.find(a=>a.code===compData.from)?.city || compData.from}` : '')}
                    onChange={e => { setRefundAirportQ(e.target.value); setRefundAirportOpen(true); if (!e.target.value) setCompData(prev => ({...(prev ?? {}), from: ''})); }}
                    onFocus={() => setRefundAirportOpen(true)}
                    onBlur={() => setTimeout(() => setRefundAirportOpen(false), 200)}
                    placeholder="Type airport name, city, or IATA code…"
                    style={{ width:'100%',height:42,padding:'0 14px',background:c.card,border:`1px solid ${c.bdr}`,borderRadius:8,fontFamily:f.b,fontSize:14,color:c.sky,outline:'none' }}
                  />
                  {refundAirportOpen && refundAirportResults.length > 0 && (
                    <div style={{ position:'absolute',left:0,right:0,top:'100%',zIndex:60,marginTop:4,maxHeight:240,overflowY:'auto',background:c.card,border:`1px solid ${c.bdr}`,borderRadius:10,boxShadow:'0 8px 32px rgba(0,0,0,.4)' }}>
                      {refundAirportResults.map(a => (
                        <button key={a.iata} onClick={() => { setCompData(prev => ({...(prev ?? {}), from: a.iata})); setRefundAirportQ(''); setRefundAirportOpen(false); }} style={{ display:'flex',alignItems:'center',gap:8,width:'100%',padding:'10px 14px',background:'none',border:'none',borderBottom:`1px solid ${c.bdr}`,cursor:'pointer',textAlign:'left',color:c.sky,fontFamily:f.b,fontSize:13 }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(43,127,255,.06)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                          <FlagImg iso={a.country_iso || ''} size={18} />
                          <span style={{ fontFamily:f.m,fontWeight:600,color:c.blueL }}>{a.iata}</span>
                          <span>{a.city || a.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {compData?.from && !refundAirportQ && (
                    <div style={{ position:'absolute',right:12,top:34,display:'flex',alignItems:'center',gap:4 }}>
                      <span style={{ fontFamily:f.m,fontSize:11,color:c.cyan }}>{compData.from}</span>
                      <button onClick={() => { setCompData(prev => ({...(prev ?? {}), from: ''})); setRefundAirportQ(''); }} style={{ background:'none',border:'none',cursor:'pointer',color:c.s50,fontSize:14,lineHeight:1 }}>✕</button>
                    </div>
                  )}
                </div>

                <div style={{ marginBottom:20,position:'relative' }}>
                  <label style={{ fontFamily:f.b,fontSize:12,fontWeight:500,color:c.s40,display:'block',marginBottom:6 }}>Airline</label>
                  <input
                    value={refundAirlineQ || (compData?.code ? `${compData.code} — ${compData.airline || compData.code}` : '')}
                    onChange={e => { setRefundAirlineQ(e.target.value); setRefundAirlineOpen(true); if (!e.target.value) setCompData(prev => ({...(prev ?? {}), code: '', airline: ''})); }}
                    onFocus={() => setRefundAirlineOpen(true)}
                    onBlur={() => setTimeout(() => setRefundAirlineOpen(false), 200)}
                    placeholder="Type airline name or IATA code…"
                    style={{ width:'100%',height:42,padding:'0 14px',background:c.card,border:`1px solid ${c.bdr}`,borderRadius:8,fontFamily:f.b,fontSize:14,color:c.sky,outline:'none' }}
                  />
                  {refundAirlineOpen && refundAirlineQ.length >= 1 && (() => {
                    const rq = refundAirlineQ.toLowerCase();
                    // Use backend airline list (allAirlinesRef) if available, fallback to local
                    const source = allAirlinesRef.length > 0
                      ? allAirlinesRef.filter(a => a.iata.toLowerCase().includes(rq) || a.name.toLowerCase().includes(rq)).slice(0, 15).map(a => ({ code: a.iata, name: a.name }))
                      : allAirlines.filter(a => a.code.toLowerCase().includes(rq) || a.name.toLowerCase().includes(rq)).slice(0, 15);
                    if (source.length === 0) return null;
                    return (
                      <div style={{ position:'absolute',left:0,right:0,top:'100%',zIndex:60,marginTop:4,maxHeight:240,overflowY:'auto',background:c.card,border:`1px solid ${c.bdr}`,borderRadius:10,boxShadow:'0 8px 32px rgba(0,0,0,.4)' }}>
                        {source.map(al => (
                          <button key={al.code} onClick={() => { setCompData(prev => ({...(prev ?? {}), code: al.code, airline: al.name})); setRefundAirlineQ(''); setRefundAirlineOpen(false); }} style={{ display:'flex',alignItems:'center',gap:8,width:'100%',padding:'10px 14px',background:'none',border:'none',borderBottom:`1px solid ${c.bdr}`,cursor:'pointer',textAlign:'left',color:c.sky,fontFamily:f.b,fontSize:13 }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(43,127,255,.06)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                            <span style={{ fontFamily:f.m,fontWeight:600,color:c.blueL,minWidth:32 }}>{al.code}</span>
                            <span>{al.name}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  {compData?.code && !refundAirlineQ && (
                    <div style={{ position:'absolute',right:12,top:34,display:'flex',alignItems:'center',gap:4 }}>
                      <span style={{ fontFamily:f.m,fontSize:11,color:c.cyan }}>{compData.code}</span>
                      <button onClick={() => { setCompData(prev => ({...(prev ?? {}), code: '', airline: ''})); setRefundAirlineQ(''); }} style={{ background:'none',border:'none',cursor:'pointer',color:c.s50,fontSize:14,lineHeight:1 }}>✕</button>
                    </div>
                  )}
                </div>

                <div style={{ marginBottom:24 }}>
                  <label style={{ fontFamily:f.b,fontSize:12,fontWeight:500,color:c.s40,display:'block',marginBottom:6 }}>Destination airport code</label>
                  <input value={compData?.to||''} onChange={e => setCompData(prev => ({...(prev ?? {}), to: e.target.value.toUpperCase()}))} placeholder="e.g. LHR" maxLength={3} style={{ width:'100%',height:42,padding:'0 14px',background:c.card,border:`1px solid ${c.bdr}`,borderRadius:8,fontFamily:f.m,fontSize:16,color:c.sky,outline:'none',letterSpacing:'.1em' }} />
                </div>

                {/* Result preview */}
                {compData?.from && compData?.code && compData?.to && (() => {
                  const dep = airports.find(a => a.code === compData.from);
                  const iso = dep?.iso || '';
                  const euCountries = ['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE','IS','NO','CH'];
                  const reg = euCountries.includes(iso) ? { name:'EU 261/2004',max:'€600',tiers:[['€250','< 1,500 km'],['€400','1,500–3,500 km'],['€600','> 3,500 km']] as [string,string][],note:'Applies to all flights departing EU/EEA airports' }
                    : iso==='GB' ? { name:'UK 261',max:'£520',tiers:[['£220','< 1,500 km'],['£350','1,500–3,500 km'],['£520','> 3,500 km']] as [string,string][],note:'Applies to flights departing UK or arriving UK on UK carriers' }
                    : iso==='TR' ? { name:'Turkey SHY Passenger',max:'€600 equiv',tiers:[['~€250','< 1,500 km'],['~€400','1,500–3,500 km'],['~€600','> 3,500 km']] as [string,string][],note:'Similar to EU 261. Applies to flights from Turkish airports' }
                    : iso==='BR' ? { name:'Brazil ANAC 400',max:'R$5,000',tiers:[['R$1,250','Up to 2 hrs delay'],['R$2,500','2–4 hrs delay'],['R$5,000','> 4 hrs delay']] as [string,string][],note:'Applies to all flights from/to Brazilian airports' }
                    : iso==='SA' ? { name:'Saudi GACA PRPR',max:'SAR 6,000',tiers:[['SAR 2,000','Domestic'],['SAR 4,000','International < 6h'],['SAR 6,000','International > 6h']] as [string,string][],note:'Applies to flights from Saudi airports' }
                    : iso==='CA' ? { name:'Canada APPR',max:'CAD 1,000',tiers:[['CAD 400','3–6 hr delay'],['CAD 700','6–9 hr delay'],['CAD 1,000','> 9 hr delay']] as [string,string][],note:'Applies to flights to/from Canada on large carriers' }
                    : iso==='IN' ? { name:'India DGCA CAR',max:'₹20,000',tiers:[['₹5,000','< 1 hr delay'],['₹10,000','1–2 hr delay'],['₹20,000','> 2 hr delay']] as [string,string][],note:'Applies to flights within or from India.' }
                    : { name:'Montreal Convention',max:'~€5,800',tiers:[['Varies','Based on actual damages'],['Up to ~€5,800','Max per passenger'],['Case-dependent','Documentation required']] as [string,string][],note:`International treaty — applies when no specific regulation exists` };
                  return (
                    <div style={{ animation:'slideUp .25s ease',marginBottom:20 }}>
                      <div style={{ background:'rgba(0,212,170,.05)',border:'1px solid rgba(0,212,170,.12)',borderRadius:14,padding:24,textAlign:'center',marginBottom:12 }}>
                        <div style={{ fontFamily:f.m,fontSize:16,color:c.sky,marginBottom:4 }}>{compData.from} → {compData.to}</div>
                        <div style={{ fontFamily:f.b,fontSize:12,color:c.s50,marginBottom:4 }}>{compData.code} · {compData.airline || compData.code}</div>
                        <div style={{ display:'inline-flex',alignItems:'center',gap:4,padding:'3px 10px',fontFamily:f.m,fontSize:10,fontWeight:600,color:c.cyan,background:'rgba(0,212,170,.08)',borderRadius:20,marginBottom:12 }}>{reg.name}</div>
                        <div style={{ fontFamily:f.b,fontSize:12,color:c.s40,marginBottom:4 }}>You may be owed up to</div>
                        <div style={{ fontFamily:f.d,fontSize:36,fontWeight:700,color:c.cyan,lineHeight:1 }}>{reg.max}</div>
                      </div>
                      <div style={{ display:'flex',gap:8 }}>
                        {reg.tiers.map(([amt,desc],i) => (
                          <div key={i} style={{ flex:1,textAlign:'center',padding:'12px 6px',background:'rgba(43,127,255,.04)',borderRadius:8,border:`1px solid ${c.bdr}` }}>
                            <div style={{ fontFamily:f.d,fontSize:16,fontWeight:700,color:c.cyan }}>{amt}</div>
                            <div style={{ fontFamily:f.m,fontSize:8,color:c.s50,marginTop:3 }}>{desc}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ fontFamily:f.b,fontSize:11,color:c.s50,marginTop:10,lineHeight:1.5 }}>{reg.note}</div>
                    </div>
                  );
                })()}

                <button disabled={!compData?.from || !compData?.code || !compData?.to} style={{ width:'100%',padding:'14px',fontFamily:f.d,fontSize:14,fontWeight:600,color:c.white,background:compData?.from && compData?.code && compData?.to ? `linear-gradient(135deg,${c.blue},${c.cyan})` : c.card,border:'none',borderRadius:10,cursor:compData?.from && compData?.code && compData?.to ? 'pointer' : 'not-allowed',opacity:compData?.from && compData?.code && compData?.to ? 1 : .4,marginBottom:8,transition:'opacity .15s' }}>
                  Start My Claim →
                </button>
                <div style={{ fontFamily:f.m,fontSize:9,color:c.s50,textAlign:'center' }}>No win, no fee · Via AirHelp</div>

                <div style={{ marginTop:28,padding:'18px 20px',background:c.card,border:`1px solid ${c.bdr}`,borderRadius:12 }}>
                  <div style={{ fontFamily:f.d,fontSize:14,fontWeight:600,color:c.white,marginBottom:12 }}>Passenger rights by region</div>
                  <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
                    {[
                      { flag:'🇪🇺', reg:'EU 261/2004', scope:'EU/EEA departures, EU carrier arrivals', max:'€600' },
                      { flag:'🇬🇧', reg:'UK 261', scope:'UK departures, UK carrier arrivals', max:'£520' },
                      { flag:'🇹🇷', reg:'Turkey SHY', scope:'Turkish airport departures', max:'~€600' },
                      { flag:'🇧🇷', reg:'Brazil ANAC 400', scope:'Brazil flights', max:'R$5,000' },
                      { flag:'🇸🇦', reg:'Saudi GACA PRPR', scope:'Saudi departures', max:'SAR 6,000' },
                      { flag:'🇨🇦', reg:'Canada APPR', scope:'To/from Canada, large carriers', max:'CAD 1,000' },
                      { flag:'🇮🇳', reg:'India DGCA CAR', scope:'Indian flights', max:'₹20,000' },
                      { flag:'🌍', reg:'Montreal Convention', scope:'International flights (fallback)', max:'~€5,800' },
                    ].map((r,i) => (
                      <div key={i} style={{ display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:i<7?`1px solid ${c.bdr}`:'none' }}>
                        <span style={{ fontSize:18,width:28,textAlign:'center' }}>{r.flag}</span>
                        <div style={{ flex:1 }}>
                          <div style={{ fontFamily:f.m,fontSize:11,fontWeight:600,color:c.blueL }}>{r.reg}</div>
                          <div style={{ fontFamily:f.b,fontSize:10.5,color:c.s50 }}>{r.scope}</div>
                        </div>
                        <span style={{ fontFamily:f.m,fontSize:12,fontWeight:600,color:c.cyan }}>{r.max}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontFamily:f.b,fontSize:10.5,color:c.s50,marginTop:12,lineHeight:1.6 }}>
                    Compensation depends on departure country, airline nationality, route distance, and disruption type. We auto-detect the applicable regulation based on your departure airport.
                  </div>
                </div>
              </div>
            )}

            {/* ═══ ALERTS ═══ */}
            {view === 'alerts' && (
              <div style={{ animation:'fadeIn .3s ease',paddingTop:40,maxWidth:560,margin:'0 auto' }}>
                <h2 style={{ fontFamily:f.d,fontSize:22,fontWeight:700,color:c.white,marginBottom:6 }}>Alerts & Advisories</h2>
                <p style={{ fontFamily:f.b,fontSize:13,color:c.s50,marginBottom:24 }}>Global flight advisory levels and your subscriptions</p>

                {/* My Subscriptions */}
                {alertKeys.length > 0 && (
                  <div style={{ marginBottom:32 }}>
                    <div style={{ fontFamily:f.m,fontSize:10,color:c.s50,textTransform:'uppercase',letterSpacing:1.5,marginBottom:10 }}>My Subscriptions</div>
                    <div style={{ display:'flex',flexDirection:'column',gap:6 }}>
                      {alertKeys.map(key => {
                        const a = airports.find(x => x.code === key);
                        return (
                          <div key={key} style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',background:c.card,border:`1px solid ${c.bdr}`,borderRadius:12 }}>
                            <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                              {a && <FlagImg iso={a.iso} size={24} />}
                              <div>
                                <div style={{ fontFamily:f.m,fontSize:13,color:c.blueL }}>{key}</div>
                                {a && <div style={{ fontFamily:f.b,fontSize:11,color:c.s50 }}>{a.city || a.name}, {a.country}</div>}
                              </div>
                              {a && <Pill s={a.status} />}
                            </div>
                            <button onClick={() => toggleAlert(key)} style={{ fontFamily:f.m,fontSize:10,color:c.red,background:'rgba(255,71,87,.08)',border:'1px solid rgba(255,71,87,.2)',borderRadius:20,padding:'4px 12px',cursor:'pointer' }}>Remove</button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Global Advisory Feed — L4 Do Not Travel */}
                {(() => {
                  const l4 = countries.filter(c2 => c2.advisory_level >= 4);
                  const l3 = countries.filter(c2 => c2.advisory_level === 3);
                  const l2 = countries.filter(c2 => c2.advisory_level === 2);
                  const l1 = countries.filter(c2 => c2.advisory_level === 1).sort((a,b) => a.name.localeCompare(b.name));
                  return (
                    <>
                      {l4.length > 0 && (
                        <div style={{ marginBottom:24 }}>
                          <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:10 }}>
                            <span style={{ width:8,height:8,borderRadius:'50%',background:c.red }} />
                            <span style={{ fontFamily:f.m,fontSize:10,color:c.red,textTransform:'uppercase',letterSpacing:1.5 }}>L4 — Do Not Travel ({l4.length})</span>
                          </div>
                          <div style={{ display:'flex',flexDirection:'column',gap:6 }}>
                            {l4.map(co => (
                              <button key={co.iso} onClick={() => { const a = airports.find(x => x.iso === co.iso); if (a) openAirport(a.code); }} style={{ display:'flex',alignItems:'center',gap:12,padding:'12px 16px',background:'rgba(255,71,87,.04)',border:'1px solid rgba(255,71,87,.15)',borderRadius:12,cursor:'pointer',textAlign:'left',width:'100%',transition:'border .15s' }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,71,87,.35)'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,71,87,.15)'; }}>
                                <span><FlagImg iso={co.iso} size={26} /></span>
                                <div style={{ flex:1 }}>
                                  <div style={{ fontFamily:f.d,fontSize:14,fontWeight:600,color:c.white }}>{co.name}</div>
                                  <div style={{ fontFamily:f.b,fontSize:11,color:c.s50 }}>{co.airports.length} airports · {co.airlines.length} airlines</div>
                                </div>
                                <span style={{ fontFamily:f.m,fontSize:10,fontWeight:700,color:c.red,background:'rgba(255,71,87,.1)',padding:'3px 10px',borderRadius:10 }}>L4</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {l3.length > 0 && (
                        <div style={{ marginBottom:24 }}>
                          <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:10 }}>
                            <span style={{ width:8,height:8,borderRadius:'50%',background:c.amber }} />
                            <span style={{ fontFamily:f.m,fontSize:10,color:c.amber,textTransform:'uppercase',letterSpacing:1.5 }}>L3 — Reconsider Travel ({l3.length})</span>
                          </div>
                          <div style={{ display:'flex',flexDirection:'column',gap:6 }}>
                            {l3.map(co => (
                              <button key={co.iso} onClick={() => { const a = airports.find(x => x.iso === co.iso); if (a) openAirport(a.code); }} style={{ display:'flex',alignItems:'center',gap:12,padding:'12px 16px',background:'rgba(255,176,32,.03)',border:'1px solid rgba(255,176,32,.12)',borderRadius:12,cursor:'pointer',textAlign:'left',width:'100%',transition:'border .15s' }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,176,32,.3)'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,176,32,.12)'; }}>
                                <span><FlagImg iso={co.iso} size={26} /></span>
                                <div style={{ flex:1 }}>
                                  <div style={{ fontFamily:f.d,fontSize:14,fontWeight:600,color:c.white }}>{co.name}</div>
                                  <div style={{ fontFamily:f.b,fontSize:11,color:c.s50 }}>{co.airports.length} airports</div>
                                </div>
                                <span style={{ fontFamily:f.m,fontSize:10,fontWeight:700,color:c.amber,background:'rgba(255,176,32,.1)',padding:'3px 10px',borderRadius:10 }}>L3</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {l2.length > 0 && (
                        <div style={{ marginBottom:24 }}>
                          <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:10 }}>
                            <span style={{ width:8,height:8,borderRadius:'50%',background:'#FFD700' }} />
                            <span style={{ fontFamily:f.m,fontSize:10,color:'#FFD700',textTransform:'uppercase',letterSpacing:1.5 }}>L2 — Exercise Caution ({l2.length})</span>
                          </div>
                          <div style={{ display:'flex',flexWrap:'wrap',gap:6 }}>
                            {l2.map(co => (
                              <button key={co.iso} onClick={() => { const a = airports.find(x => x.iso === co.iso); if (a) openAirport(a.code); }} style={{ display:'inline-flex',alignItems:'center',gap:6,padding:'8px 14px',background:'rgba(255,215,0,.03)',border:'1px solid rgba(255,215,0,.1)',borderRadius:10,cursor:'pointer',fontFamily:f.b,fontSize:12,color:c.sky,transition:'border .15s' }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,215,0,.25)'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,215,0,.1)'; }}>
                                <FlagImg iso={co.iso} size={16} /> {co.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {l1.length > 0 && (
                        <div style={{ marginBottom:24 }}>
                          <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:10 }}>
                            <span style={{ width:8,height:8,borderRadius:'50%',background:c.green }} />
                            <span style={{ fontFamily:f.m,fontSize:10,color:c.green,textTransform:'uppercase',letterSpacing:1.5 }}>L1 — Normal Precautions ({l1.length})</span>
                          </div>
                          <div style={{ display:'flex',flexWrap:'wrap',gap:4 }}>
                            {l1.slice(0, 40).map(co => (
                              <span key={co.iso} style={{ display:'inline-flex',alignItems:'center',gap:4,padding:'5px 10px',background:'rgba(0,196,140,.03)',border:`1px solid ${c.bdr}`,borderRadius:8,fontFamily:f.b,fontSize:11,color:c.s40 }}>
                                <FlagImg iso={co.iso} size={16} /> {co.name}
                              </span>
                            ))}
                            {l1.length > 40 && (
                              <span style={{ padding:'5px 10px',fontFamily:f.m,fontSize:11,color:c.s50 }}>+{l1.length - 40} more</span>
                            )}
                          </div>
                        </div>
                      )}

                      {countries.length === 0 && alertKeys.length === 0 && (
                        <div style={{ textAlign:'center',padding:'60px 0' }}>
                          <div style={{ color:c.s50,marginBottom:12 }}><Ic.Bell s={28} /></div>
                          <div style={{ fontFamily:f.d,fontSize:15,color:c.s40,marginBottom:6 }}>No advisory data available</div>
                          <div style={{ fontFamily:f.b,fontSize:12,color:c.s50,marginBottom:20 }}>Check your connection and refresh</div>
                          <button onClick={goHome} style={{ padding:'8px 20px',fontFamily:f.b,fontSize:13,color:c.blue,background:'rgba(43,127,255,.08)',border:`1px solid rgba(43,127,255,.2)`,borderRadius:8,cursor:'pointer' }}>Find Airports</button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* ═══ CRISES ═══ */}
            {view === 'crises' && (
              <div style={{ animation:'fadeIn .3s ease',paddingTop:40,maxWidth:520,margin:'0 auto' }}>
                <h2 style={{ fontFamily:f.d,fontSize:22,fontWeight:700,color:c.white,marginBottom:6 }}>Active Crises</h2>
                <p style={{ fontFamily:f.b,fontSize:13,color:c.s50,marginBottom:24 }}>Aviation disruptions being monitored</p>
                {crises.length === 0 ? (
                  // Fallback hardcoded crisis when API returns none
                  <div style={{ background:c.card,border:`1px solid ${c.bdr}`,borderRadius:12,padding:'20px 22px' }}>
                    <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:12 }}>
                      <span style={{ width:8,height:8,borderRadius:'50%',background:c.red }} />
                      <span style={{ fontFamily:f.d,fontSize:17,fontWeight:700,color:c.white }}>Middle East Airspace Crisis</span>
                    </div>
                    <div style={{ fontFamily:f.b,fontSize:13,color:c.s40,lineHeight:1.6,marginBottom:16 }}>Armed conflict affecting airspace across Iran, Israel, Iraq, and GCC states.</div>
                    <div style={{ display:'flex',gap:20,marginBottom:16 }}>
                      {([['Closed',airports.filter(a=>a.status==='closed').length,c.red],['Partial',airports.filter(a=>a.status==='partial').length,c.amber],['Open',airports.filter(a=>a.status==='open').length,c.green]] as [string,number,string][]).map(([l,v,col]) => (
                        <div key={l}><div style={{ fontFamily:f.d,fontSize:24,fontWeight:700,color:col }}>{v}</div><div style={{ fontFamily:f.m,fontSize:9,color:c.s50 }}>{l}</div></div>
                      ))}
                    </div>
                    <div style={{ fontFamily:f.m,fontSize:10,color:c.s50,marginBottom:10 }}>IMPACTED COUNTRIES</div>
                    <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                      {[...new Map(airports.map(a => [a.iso, { flag:a.flag, country:a.country, iso:a.iso }])).values()].map(o => (
                        <span key={o.iso} style={{ display:'inline-flex',alignItems:'center',gap:4,padding:'4px 10px',background:'rgba(255,255,255,.03)',border:`1px solid ${c.bdr}`,borderRadius:20,fontFamily:f.b,fontSize:11,color:c.s40 }}>
                          {o.flag} {o.country}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ display:'flex',flexDirection:'column',gap:16 }}>
                    {crises.map(crisis => (
                      <div key={crisis.slug} style={{ background:c.card,border:`1px solid ${c.bdr}`,borderRadius:12,padding:'20px 22px' }}>
                        <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:10 }}>
                          <span style={{ width:8,height:8,borderRadius:'50%',background:crisis.severity >= 3 ? c.red : c.amber }} />
                          <span style={{ fontFamily:f.d,fontSize:16,fontWeight:700,color:c.white }}>{crisis.name}</span>
                          <span style={{ fontFamily:f.m,fontSize:9,color:crisis.severity>=3?c.red:c.amber,background:crisis.severity>=3?'rgba(255,71,87,.08)':'rgba(255,176,32,.08)',padding:'2px 8px',borderRadius:10 }}>L{crisis.severity}</span>
                        </div>
                        <div style={{ fontFamily:f.b,fontSize:13,color:c.s40,lineHeight:1.6,marginBottom:14 }}>{crisis.description}</div>
                        {crisis.countries.length > 0 && (
                          <>
                            <div style={{ fontFamily:f.m,fontSize:10,color:c.s50,marginBottom:8 }}>IMPACTED COUNTRIES</div>
                            <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                              {crisis.countries.map(iso => (
                                <span key={iso} style={{ display:'inline-flex',alignItems:'center',gap:4,padding:'4px 10px',background:'rgba(255,255,255,.03)',border:`1px solid ${c.bdr}`,borderRadius:20,fontFamily:f.b,fontSize:11,color:c.s40 }}>
                                  <FlagImg iso={iso} size={14} /> {iso}
                                </span>
                              ))}
                            </div>
                          </>
                        )}
                        {crisis.airports.length > 0 && (
                          <div style={{ marginTop:12 }}>
                            <div style={{ fontFamily:f.m,fontSize:10,color:c.s50,marginBottom:8 }}>AFFECTED AIRPORTS</div>
                            <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                              {crisis.airports.map(iata => (
                                <button key={iata} onClick={() => openAirport(iata)} style={{ display:'inline-flex',alignItems:'center',gap:4,padding:'4px 10px',background:'rgba(43,127,255,.05)',border:`1px solid ${c.bdr}`,borderRadius:20,fontFamily:f.m,fontSize:11,color:c.blueL,cursor:'pointer',transition:'border .1s' }}
                                  onMouseEnter={e => { e.currentTarget.style.borderColor = c.bdrH; }}
                                  onMouseLeave={e => { e.currentTarget.style.borderColor = c.bdr; }}>
                                  {iata}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        <div style={{ fontFamily:f.m,fontSize:10,color:c.s50,marginTop:14 }}>Updated {new Date(crisis.last_updated).toLocaleDateString()}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ═══ FEEDBACK ═══ */}
            {view === 'feedback' && (
              <div style={{ animation:'fadeIn .3s ease',paddingTop:40,maxWidth:560,margin:'0 auto' }}>
                <h2 style={{ fontFamily:f.d,fontSize:22,fontWeight:700,color:c.white,marginBottom:4 }}>Help us improve</h2>
                <p style={{ fontFamily:f.b,fontSize:13,color:c.s50,marginBottom:28,lineHeight:1.6 }}>
                  FlightsOpen is built for travelers in crisis. Your feedback directly shapes what we build next. Suggest features, report bugs, or vote on what matters most.
                </p>

                <div style={{ background:c.card,border:`1px solid ${c.bdr}`,borderRadius:14,padding:'20px',marginBottom:28 }}>
                  <div style={{ fontFamily:f.d,fontSize:14,fontWeight:600,color:c.white,marginBottom:14 }}>Submit feedback</div>
                  <div style={{ display:'flex',gap:6,marginBottom:14 }}>
                    {[{id:'suggestion',l:'💡 Suggestion'},{id:'bug',l:'🐛 Bug Report'},{id:'other',l:'💬 Other'}].map(t2 => (
                      <button key={t2.id} onClick={() => setFbType(t2.id)} style={{ padding:'6px 14px',fontFamily:f.b,fontSize:12,color:fbType===t2.id?c.white:c.s50,background:fbType===t2.id?'rgba(43,127,255,.12)':'none',border:`1px solid ${fbType===t2.id?'rgba(43,127,255,.25)':c.bdr}`,borderRadius:20,cursor:'pointer',transition:'all .15s' }}>
                        {t2.l}
                      </button>
                    ))}
                  </div>
                  <textarea value={fbText} onChange={e => setFbText(e.target.value)} placeholder={fbType==='bug' ? 'Describe the bug — what happened and what you expected...' : 'Describe your idea — how would this help you as a traveler?'} rows={4} style={{ width:'100%',background:c.bg,border:`1px solid ${c.bdr}`,borderRadius:10,padding:'12px 14px',fontFamily:f.b,fontSize:13,color:c.sky,outline:'none',resize:'vertical',lineHeight:1.6 }} />
                  <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:12 }}>
                    <span style={{ fontFamily:f.b,fontSize:11,color:c.s50 }}>Sent to support@flightsopen.ai</span>
                    <button onClick={() => {
                      if (!fbText.trim()) return;
                      const newItem = { id:Date.now(), type:fbType, text:fbText.trim(), votes:1, voted:true, ts:'Just now' };
                      setFbItems(prev => [newItem, ...prev]);
                      setFbText('');
                      setFbSent(true);
                      setTimeout(() => setFbSent(false), 3000);
                    }} disabled={!fbText.trim()} style={{ display:'flex',alignItems:'center',gap:5,padding:'8px 18px',fontFamily:f.d,fontSize:13,fontWeight:600,color:c.white,background:fbText.trim()?`linear-gradient(135deg,${c.blue},${c.cyan})`:c.card,border:'none',borderRadius:8,cursor:fbText.trim()?'pointer':'not-allowed',opacity:fbText.trim()?1:.4,transition:'all .15s' }}>
                      <Ic.Send s={13} /> Submit
                    </button>
                  </div>
                  {fbSent && (
                    <div style={{ marginTop:10,display:'flex',alignItems:'center',gap:6,padding:'8px 12px',background:'rgba(0,196,140,.08)',border:'1px solid rgba(0,196,140,.15)',borderRadius:8,fontFamily:f.b,fontSize:12,color:c.green,animation:'slideUp .2s ease' }}>
                      <Ic.Check s={14} /> Feedback submitted — thank you! We'll review it shortly.
                    </div>
                  )}
                </div>

                <div style={{ fontFamily:f.d,fontSize:14,fontWeight:600,color:c.white,marginBottom:4 }}>Community suggestions</div>
                <p style={{ fontFamily:f.b,fontSize:12,color:c.s50,marginBottom:16 }}>Vote on what you'd like us to build next</p>

                <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
                  {fbItems.sort((a,b) => b.votes - a.votes).map(item => (
                    <div key={item.id} style={{ display:'flex',alignItems:'flex-start',gap:12,padding:'14px 16px',background:c.card,border:`1px solid ${c.bdr}`,borderRadius:12 }}>
                      <button onClick={() => setFbItems(prev => prev.map(fi => fi.id===item.id ? {...fi, votes:fi.voted?fi.votes-1:fi.votes+1, voted:!fi.voted} : fi))} style={{ display:'flex',flexDirection:'column',alignItems:'center',gap:2,padding:'6px 8px',background:item.voted?'rgba(43,127,255,.1)':'none',border:`1px solid ${item.voted?'rgba(43,127,255,.25)':c.bdr}`,borderRadius:8,cursor:'pointer',transition:'all .15s',flexShrink:0,minWidth:44 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={item.voted?c.blue:c.s50} strokeWidth="2.5" strokeLinecap="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
                        <span style={{ fontFamily:f.m,fontSize:12,fontWeight:700,color:item.voted?c.blue:c.s40 }}>{item.votes}</span>
                      </button>
                      <div style={{ flex:1 }}>
                        <div style={{ display:'flex',alignItems:'center',gap:6,marginBottom:4 }}>
                          <span style={{ fontFamily:f.m,fontSize:9,color:item.type==='bug'?c.red:c.blue,background:item.type==='bug'?'rgba(255,71,87,.08)':'rgba(43,127,255,.08)',padding:'2px 7px',borderRadius:10 }}>
                            {item.type==='bug'?'🐛 Bug':'💡 Idea'}
                          </span>
                          <span style={{ fontFamily:f.m,fontSize:9,color:c.s50 }}>{item.ts}</span>
                        </div>
                        <div style={{ fontFamily:f.b,fontSize:13,color:c.sky,lineHeight:1.5 }}>{item.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* ═══ COMPENSATION MODAL ═══ */}
      {compData && (
        <div style={{ position:'fixed',inset:0,zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',animation:'fadeIn .2s ease' }}>
          <div style={{ position:'absolute',inset:0,background:'rgba(11,17,32,.85)' }} onClick={() => setCompData(null)} />
          <div style={{ position:'relative',width:400,maxWidth:'90vw',background:c.card,border:`1px solid ${c.bdr}`,borderRadius:16,animation:'slideUp .25s ease',overflow:'hidden' }}>
            <div style={{ padding:'20px 24px' }}>
              <div style={{ display:'flex',justifyContent:'space-between',marginBottom:20 }}>
                <h3 style={{ fontFamily:f.d,fontSize:17,fontWeight:600,color:c.white }}>Check Compensation</h3>
                <button onClick={() => setCompData(null)} style={{ background:'none',border:'none',cursor:'pointer' }}><Ic.X /></button>
              </div>
              <div style={{ fontFamily:f.m,fontSize:20,color:c.sky,marginBottom:2 }}>{compData.from} → {compData.to}</div>
              <div style={{ fontFamily:f.b,fontSize:13,color:c.s50,marginBottom:20 }}>{compData.code} · {compData.airline} · Suspended</div>
              <div style={{ background:'rgba(0,212,170,.06)',border:'1px solid rgba(0,212,170,.12)',borderRadius:12,padding:20,textAlign:'center',marginBottom:20 }}>
                <div style={{ fontFamily:f.b,fontSize:12,color:c.s40,marginBottom:4 }}>You may be owed up to</div>
                <div style={{ fontFamily:f.d,fontSize:36,fontWeight:700,color:c.cyan }}>€600</div>
                <div style={{ fontFamily:f.m,fontSize:10,color:c.s50,marginTop:4 }}>Under EU 261 · Routes over 3,500 km</div>
              </div>
              <button style={{ width:'100%',padding:'14px',fontFamily:f.d,fontSize:14,fontWeight:600,color:c.white,background:`linear-gradient(135deg,${c.blue},${c.cyan})`,border:'none',borderRadius:10,cursor:'pointer',marginBottom:8 }}>Start My Claim →</button>
              <div style={{ fontFamily:f.m,fontSize:9,color:c.s50,textAlign:'center' }}>No win, no fee · Via AirHelp</div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ FLOATING NAVIGATOR AI BUTTON ═══ */}
      {!chatOpen && (
        <button onClick={() => setChatOpen(true)} style={{ position:'fixed',bottom:24,right:24,zIndex:100,display:'flex',alignItems:'center',gap:8,padding:'14px 20px',background:`linear-gradient(135deg,${c.blue},${c.cyan})`,border:'none',borderRadius:50,cursor:'pointer',boxShadow:'0 4px 24px rgba(0,212,170,.3), 0 2px 8px rgba(0,0,0,.3)',transition:'all .2s',animation:'slideUp .4s ease both .3s',fontFamily:f.d,fontSize:13,fontWeight:600,color:c.white,letterSpacing:'-.2px' }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)'; e.currentTarget.style.boxShadow = '0 6px 32px rgba(0,212,170,.4), 0 4px 12px rgba(0,0,0,.3)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,212,170,.3), 0 2px 8px rgba(0,0,0,.3)'; }}>
          <Ic.Compass s={18} /> Navigator AI
        </button>
      )}

      {/* ═══ NAVIGATOR AI CHAT PANEL ═══ */}
      {chatOpen && (
        <div style={{ position:'fixed',bottom:24,right:24,width:390,maxWidth:'calc(100vw - 48px)',height:480,zIndex:100,display:'flex',flexDirection:'column',background:c.card,border:`1px solid rgba(0,212,170,.2)`,borderRadius:20,boxShadow:'0 8px 48px rgba(0,0,0,.5), 0 0 80px rgba(0,212,170,.04)',animation:'slideUp .25s ease',overflow:'hidden' }}>
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 18px',borderBottom:'1px solid rgba(0,212,170,.1)',background:'rgba(0,212,170,.02)' }}>
            <div style={{ display:'flex',alignItems:'center',gap:10 }}>
              <div style={{ width:32,height:32,borderRadius:10,background:`linear-gradient(135deg,${c.blue},${c.cyan})`,display:'flex',alignItems:'center',justifyContent:'center' }}><Ic.Compass s={16} /></div>
              <div>
                <div style={{ fontFamily:f.d,fontSize:14,fontWeight:700,color:c.white }}>Navigator AI</div>
                <div style={{ fontFamily:f.m,fontSize:8,color:c.cyan,letterSpacing:'.5px' }}>CONFIRMED DATA ONLY</div>
              </div>
            </div>
            <div style={{ display:'flex',alignItems:'center',gap:4 }}>
              {chatMsgs.length > 0 && (
                <button onClick={() => setChatMsgs([])} title="Clear conversation" style={{ background:'none',border:`1px solid ${c.bdr}`,borderRadius:6,cursor:'pointer',padding:'4px 8px',display:'flex',alignItems:'center',gap:4,fontFamily:f.m,fontSize:9,color:c.s40,transition:'all .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = c.bdrH; e.currentTarget.style.color = c.white; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = c.bdr; e.currentTarget.style.color = c.s40; }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
                  Clear
                </button>
              )}
              <button onClick={() => setChatOpen(false)} style={{ background:'none',border:'none',cursor:'pointer',padding:4 }}><Ic.X /></button>
            </div>
          </div>
          <div style={{ flex:1,padding:'14px 16px',overflowY:'auto',display:'flex',flexDirection:'column',gap:10 }}>
            {chatMsgs.length === 0 ? (
              <>
                <div style={{ padding:'12px 14px',fontFamily:f.b,fontSize:13,color:c.sky,lineHeight:1.6,borderLeft:`2px solid ${c.cyan}`,background:'rgba(0,212,170,.03)',borderRadius:'0 10px 10px 0' }}>
                  I find open flights and escape routes from confirmed airline data. What do you need?
                </div>
                <div style={{ display:'flex',flexDirection:'column',gap:6,marginTop:4 }}>
                  {["What's flying from Dubai?", "I'm stuck in Tehran", "Am I owed compensation?"].map((suggestion, i) => (
                    <button key={i} onClick={() => {
                      setChatMsgs([{ r:'u', t:suggestion }]);
                      setChatLoading(true);
                      sendChatMessage(suggestion)
                        .then(res => setChatMsgs(p => [...p, { r:'a', t:res.reply }]))
                        .catch(() => setChatMsgs(p => [...p, { r:'a', t:"Sorry, I couldn't connect right now." }]))
                        .finally(() => setChatLoading(false));
                    }} style={{ textAlign:'left',padding:'11px 14px',fontFamily:f.b,fontSize:13,color:c.blueL,background:'rgba(43,127,255,.05)',border:`1px solid ${c.bdr}`,borderRadius:12,cursor:'pointer',transition:'border .1s',lineHeight:1.4 }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = c.bdrH; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = c.bdr; }}>
                      {suggestion}
                    </button>
                  ))}
                </div>
              </>
            ) : chatMsgs.map((m, i) => m.r === 'u' ? (
              <div key={i} style={{ alignSelf:'flex-end',maxWidth:'82%',padding:'10px 14px',background:c.blue,borderRadius:'12px 12px 2px 12px',fontFamily:f.b,fontSize:13,color:c.white,lineHeight:1.5 }}>{m.t}</div>
            ) : (
              <div key={i} style={{ padding:'12px 14px',fontFamily:f.b,fontSize:13,color:c.sky,lineHeight:1.65,borderLeft:`2px solid ${c.cyan}`,background:'rgba(0,212,170,.03)',borderRadius:'0 10px 10px 0',whiteSpace:'pre-wrap' }}>{m.t}</div>
            ))}
            {chatLoading && (
              <div style={{ padding:'12px 14px',fontFamily:f.b,fontSize:13,color:c.s50,lineHeight:1.65,borderLeft:`2px solid ${c.cyan}`,background:'rgba(0,212,170,.03)',borderRadius:'0 10px 10px 0',display:'flex',alignItems:'center',gap:8 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c.cyan} strokeWidth="2" strokeLinecap="round" style={{ animation:'spin .8s linear infinite',flexShrink:0 }}><path d="M12 2v4m0 12v4m10-10h-4M6 12H2"/></svg>
                Thinking…
              </div>
            )}
          </div>
          <div style={{ padding:'12px 16px',borderTop:'1px solid rgba(0,212,170,.1)' }}>
            <div style={{ display:'flex',alignItems:'center',gap:8,padding:'0 12px',height:42,background:c.bg,border:`1px solid ${c.bdr}`,borderRadius:12 }}>
              <input value={chatMsg} onChange={e => setChatMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && doChat()} placeholder="Ask anything..." style={{ flex:1,background:'none',border:'none',outline:'none',fontFamily:f.b,fontSize:13,color:c.sky }} />
              <button onClick={doChat} disabled={chatLoading} style={{ background:`linear-gradient(135deg,${c.blue},${c.cyan})`,border:'none',borderRadius:8,width:34,height:34,display:'flex',alignItems:'center',justifyContent:'center',cursor:chatLoading?'wait':'pointer',flexShrink:0,transition:'transform .1s',opacity:chatLoading?.6:1 }}
                onMouseDown={e => { e.currentTarget.style.transform = 'scale(.95)'; }}
                onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                <Ic.Send />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
